import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { HelpCircle, ChevronDown, ChevronUp, Zap, Copy, Check, Lock } from 'lucide-react';
import BrandIcon from './BrandIcon';
import Popover from './Popover';
import { useToast } from './Toast';
import {
  Input, PasswordInput, Select, Label, FormGroup, Flex,
  ModalOverlay, ModalBox, ModalHeader, ModalTitle, ModalBody, ModalFooter, Button, Badge,
  FieldHint, SectionLabel, ToggleRow, ToggleLabel, ToggleDesc, ToggleSwitch, ToggleThumb,
} from './ui';
import { theme } from '../styles/theme';
import api from '../services/api';
import { getDisplayableTools, toggleTool as toggleToolUtil, mergeEnabledActions, cleanupAfterRemoval } from '../utils/enabled-actions';
import WorkingHoursEditor, { whFromBackend, whToBackend } from './WorkingHoursEditor';
import SchedulingRulesEditor, { findSchedulingRulesErrors } from './agents/SchedulingRulesEditor';
import WhatsAppConnectButton from './connectors/WhatsAppConnectButton';

// Canais que suportam transferência para humano — só esses expõem o horário de
// atendimento (o backend usa o workingHours do conector de origem como fallback
// quando os membros não têm horário próprio).
const HANDOFF_CHANNEL_TYPES = ['whatsapp', 'telegram', 'webchat', 'instagram', 'sms', 'voice'];

// Categorias canônicas de agente (alinhadas a
// backend/src/constants/agent-categories.js). `interno` é para agentes de
// sistema e não aparece no site público.
const AGENT_CATEGORY_OPTIONS = [
  { value: 'saude', labelKey: 'category.saude' },
  { value: 'atendimento', labelKey: 'category.atendimento' },
  { value: 'vendas', labelKey: 'category.vendas' },
  { value: 'suporte', labelKey: 'category.suporte' },
  { value: 'marketing', labelKey: 'category.marketing' },
  { value: 'financeiro', labelKey: 'category.financeiro' },
  { value: 'operacoes', labelKey: 'category.operacoes' },
  { value: 'juridico', labelKey: 'category.juridico' },
  { value: 'rh', labelKey: 'category.rh' },
  { value: 'ti', labelKey: 'category.ti' },
  { value: 'produtividade', labelKey: 'category.produtividade' },
  { value: 'imobiliario', labelKey: 'category.imobiliario' },
  { value: 'ecommerce', labelKey: 'category.ecommerce' },
  { value: 'pessoal', labelKey: 'category.pessoal' },
  { value: 'geral', labelKey: 'category.geral' },
  { value: 'interno', labelKey: 'category.interno' },
];

const AgentForm = ({
  formData, setFormData,
  isConfiguring = false,
  isSallusAdmin = false,
  agentInstanceId,
  agentInstanceName,
  tenantId,
  allConnectorTypes = [],
  agentConnectors = [],
  onConnectorsChange,
  tenantCredentials = [],
  onCredentialsChange,
  templates = [],
  onInstanceTemplate,
  initialTab,
  mediaFeatures = {},
}) => {
  const { t } = useTranslation(['agents', 'common']);
  const toast = useToast();
  const { i18n } = useTranslation();

  const r = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val[i18n.language] || val['pt-BR'] || val['en'] || Object.values(val)[0] || '';
  };

  // ── dynamic model list from API ──────────────────────────────────────────
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchModels = async () => {
      setModelsLoading(true);
      setModelsError(null);
      try {
        const res = await api.get('/admin/credit-costs', { params: { category: 'llm' } });
        if (!cancelled) {
          const models = (res.data.items || res.data || []).map(item => ({
            value: item.modelId || item.serviceId,
            label: `${item.displayName} (${item.creditsPerUnit} ${t('creditsPerMessage', { defaultValue: 'créditos/msg' })})`,
            displayName: item.displayName,
            creditsPerUnit: item.creditsPerUnit,
          }));
          setAvailableModels(models);
        }
      } catch (err) {
        if (!cancelled) {
          setModelsError(t('modelsLoadError', { defaultValue: 'Erro ao carregar modelos. Tente recarregar a página.' }));
        }
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    };
    fetchModels();
    return () => { cancelled = true; };
  }, [t]);

  // ── connector type modal ──────────────────────────────────────────────────
  const [ctModal, setCtModal] = useState(null);
  const [ctForm, setCtForm] = useState({});
  const [ctSaving, setCtSaving] = useState(false);
  const [removingConnectorId, setRemovingConnectorId] = useState(null);

  const getGoogleAuthUrl = () => {
    const token = sessionStorage.getItem('idToken') || localStorage.getItem('idToken') || '';
    return `${process.env.REACT_APP_API_URL}/connectors/${tenantId}/google/auth?token=${encodeURIComponent(token)}`;
  };
  const [expandedTools, setExpandedTools] = useState({});
  const [copiedWebhook, setCopiedWebhook] = useState(null);
  // Modo de exibição do snippet de webchat, por conector:
  // 'button' (flutuante com botão) | 'floating' (flutuante sem botão) | 'inline' (embutido)
  const [webchatMode, setWebchatMode] = useState({});
  const [rightExpanded, setRightExpanded] = useState(false); // mantido para compatibilidade
  const [activeTab, setActiveTab] = useState(initialTab || 'identity');
  const [basePromptExpanded, setBasePromptExpanded] = useState(false);
  const [publicLinkAdvanced, setPublicLinkAdvanced] = useState(false);
  // Texto cru do campo de tags. Mantido separado de formData.tags (array) para
  // permitir digitar a vírgula sem que o input controlado a descarte enquanto
  // o usuário escreve. Convertido para array no onBlur.
  const [tagsText, setTagsText] = useState('');
  useEffect(() => {
    // Sincroniza quando o form muda de fora (abrir modal com outro agente).
    setTagsText((formData.tags || []).join(', '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.agentId]);
  const [credModalType, setCredModalType] = useState(null);
  const [credModalName, setCredModalName] = useState('');
  const [credSaving, setCredSaving] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  const getWebhookUrl = (ct, connector) => {
    if (ct?.capabilities?.inbound?.type !== 'webhook') return null;
    const base = `${API_BASE}/webhooks/${ct.type}/${tenantId}`;
    return connector?.connectorId ? `${base}?connectorId=${connector.connectorId}` : base;
  };

  const copyWebhook = (e, url) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    setCopiedWebhook(url);
    setTimeout(() => setCopiedWebhook(null), 2000);
  };

  const toggleToolsExpand = (e, type) => {
    e.stopPropagation();
    setExpandedTools(p => ({ ...p, [type]: !p[type] }));
  };

  const openCtModal = (ct) => {
    if (!tenantId) {
      const list = formData.connectors || [];
      const exists = list.find(c => c.type === ct.type);
      setFormData({ ...formData, connectors: exists ? list.filter(c => c.type !== ct.type) : [...list, { type: ct.type, name: r(ct.name) }] });
      return;
    }
    const existing = agentConnectors.find(c => c.type === ct.type);
    const initial = { 
      name: existing?.name || `${r(ct.name)}-${agentInstanceName || agentInstanceId || ''}`, 
      enabled: existing?.enabled !== false
    };
    (ct.configFields || []).forEach(f => { 
      if (f.name in (existing || {})) {
        initial[f.name] = existing[f.name];
      } else if (f.default !== undefined) {
        initial[f.name] = f.default;
      } else if (f.type === 'boolean') {
        initial[f.name] = false;
      } else {
        initial[f.name] = '';
      }
    });
    // Horário de atendimento (Feature B) — só para canais que suportam handoff humano.
    // Carrega o valor persistido no conector para o formato do editor.
    if (HANDOFF_CHANNEL_TYPES.includes(ct.type)) {
      initial.workingHours = whFromBackend(existing?.workingHours);
      initial.afterHoursHandoffMessage = existing?.afterHoursHandoffMessage || '';
    }
    // Regras de agendamento — só para conectores que expõem o catálogo (ex: Feegow)
    if (Array.isArray(ct.schedulingRuleCatalog) && ct.schedulingRuleCatalog.length > 0) {
      initial.schedulingRules = Array.isArray(existing?.schedulingRules) ? existing.schedulingRules : [];
    }
    setCtForm(initial);
    setCtModal({ connectorType: ct, existingConnector: existing || null });
  };

  const handleCtSave = async () => {
    if (!ctModal) return;
    if (!tenantId) {
      const list = formData.connectors || [];
      const exists = list.find(c => c.type === ctModal.connectorType.type);
      setFormData({ ...formData, connectors: exists ? list.map(c => c.type === ctModal.connectorType.type ? { ...c, ...ctForm } : c) : [...list, { type: ctModal.connectorType.type, ...ctForm }] });
      setCtModal(null); return;
    }
    // Bloqueia save se houver regras de agendamento incompletas (backend também valida)
    if (Array.isArray(ctForm.schedulingRules) && ctForm.schedulingRules.length > 0) {
      const ruleErrs = findSchedulingRulesErrors(ctForm.schedulingRules);
      if (ruleErrs.length > 0) {
        toast.error(`${t('schedulingRules.invalidRules', { defaultValue: 'Regras de agendamento inválidas' })}: ${ruleErrs.join('; ')}`);
        return;
      }
    }
    setCtSaving(true);
    try {
      const { connectorType, existingConnector } = ctModal;
      const connectorId = existingConnector?.connectorId || `${connectorType.type}_${Date.now()}`;
      const payload = { connectorId, type: connectorType.type, agentInstanceId, ...ctForm, enabled: true };
      // Converte o horário de atendimento do formato do editor para o do backend
      // ({days:{mon:[{start,end}]}}); null quando desativado (limpa o horário).
      if ('workingHours' in payload) {
        payload.workingHours = whToBackend(ctForm.workingHours);
      }
      if (existingConnector) await api.put(`/connectors/${tenantId}/${connectorId}`, payload);
      else await api.post(`/connectors/${tenantId}`, payload);

      // Auto-enable tools for this connector (fire-and-forget, mirrors B2CDashboard pattern)
      if (tenantId && agentInstanceId) {
        try {
          const agentRes = await api.get(`/marketplace/tenants/${tenantId}/agents/${agentInstanceId}`);
          const currentActions = agentRes.data.enabledActions || [];
          const merged = mergeEnabledActions(currentActions, connectorType, existingConnector);
          await api.put(`/marketplace/tenants/${tenantId}/agents/${agentInstanceId}`, {
            enabledActions: merged,
          });
          // Update local formData to reflect the new enabledActions
          setFormData(prev => ({ ...prev, enabledActions: merged }));
        } catch (err) {
          console.error('Failed to update enabledActions (non-fatal):', err.message);
        }
      }

      onConnectorsChange?.(); setCtModal(null);
      // Warning se conector OAuth ainda não foi autorizado
      const needsOAuth = ['google_workspace', 'microsoft_graph'].includes(connectorType.type) && !ctForm.accessToken;
      if (needsOAuth) {
        toast.warning(t('connectorSavedNeedsOAuth', { defaultValue: 'Conector salvo! Clique em "Conectar com Google/Microsoft" para autorizar o acesso.' }));
      } else {
        toast.success(t('save', { ns: 'common' }));
      }
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
    finally { setCtSaving(false); }
  };

  const handleCtRemove = async (connector) => {
    if (!tenantId) { setFormData({ ...formData, connectors: (formData.connectors || []).filter(c => c.type !== connector.type) }); return; }
    setRemovingConnectorId(connector.connectorId);
    try {
      await api.delete(`/connectors/${tenantId}/${connector.connectorId}`);

      // Fire-and-forget: clean up enabledActions after connector removal
      if (tenantId && agentInstanceId) {
        try {
          const removedCt = allConnectorTypes.find(ct => ct.type === connector.type);
          const remaining = agentConnectors.filter(c => c.connectorId !== connector.connectorId);
          const agentRes = await api.get(`/marketplace/tenants/${tenantId}/agents/${agentInstanceId}`);
          const currentActions = agentRes.data.enabledActions || [];
          const cleaned = cleanupAfterRemoval(currentActions, removedCt, remaining, allConnectorTypes);
          await api.put(`/marketplace/tenants/${tenantId}/agents/${agentInstanceId}`, {
            enabledActions: cleaned,
          });
          setFormData(prev => ({ ...prev, enabledActions: cleaned }));
        } catch (err) {
          console.error('Failed to cleanup enabledActions after removal (non-fatal):', err.message);
        }
      }

      onConnectorsChange?.();
    }
    catch (e) { toast.error(e.response?.data?.error || e.message); }
    finally { setRemovingConnectorId(null); }
  };

  // ── template instanciation modal ────────────────────────────────────────────
  const [templateModal, setTemplateModal] = useState(null); // template object or null
  const [templateDestinations, setTemplateDestinations] = useState(['']);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateError, setTemplateError] = useState('');

  const openTemplateModal = (tpl) => {
    setTemplateModal(tpl);
    setTemplateDestinations(['']);
    setTemplateError('');
  };

  const getTemplateInputConfig = (tpl) => {
    if (!tpl) return { placeholder: '', type: 'text', label: 'Destinatário' };
    const t2 = tpl.type?.toLowerCase();
    if (t2 === 'whatsapp' || t2 === 'sms') {
      return { placeholder: '+55 11 99999-0001', type: 'tel', label: 'Telefone autorizado', multipleAllowed: false };
    }
    if (t2 === 'email') {
      return { placeholder: 'cliente@empresa.com', type: 'email', label: 'Email autorizado', multipleAllowed: true };
    }
    if (t2 === 'telegram') {
      return { placeholder: '123456789', type: 'text', label: 'Chat ID autorizado', multipleAllowed: false };
    }
    return { placeholder: '', type: 'text', label: 'Destinatário autorizado', multipleAllowed: false };
  };

  const validateDestination = (value, tplType) => {
    if (!value || !value.trim()) return false;
    const t2 = tplType?.toLowerCase();
    if (t2 === 'email') {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
    }
    if (t2 === 'whatsapp' || t2 === 'sms') {
      const digits = value.replace(/\D/g, '');
      return digits.length >= 10 && digits.length <= 15;
    }
    if (t2 === 'telegram') {
      return value.trim().length > 0;
    }
    return value.trim().length > 0;
  };

  const handleTemplateInstanciate = async () => {
    if (!templateModal || !tenantId || !agentInstanceId) return;
    const validDests = templateDestinations.filter(d => d.trim());
    if (validDests.length === 0) {
      setTemplateError(t('templateNoDestination', { defaultValue: 'Informe ao menos um destinatário.' }));
      return;
    }
    // Validate each destination
    const invalidDest = validDests.find(d => !validateDestination(d, templateModal.type));
    if (invalidDest) {
      setTemplateError(t('templateInvalidDestination', { defaultValue: 'Destinatário inválido: ' }) + invalidDest);
      return;
    }
    setTemplateSaving(true);
    setTemplateError('');
    try {
      await api.post(`/connectors/${tenantId}`, {
        sourceTemplateId: templateModal.templateId,
        agentInstanceId,
        allowedDestinations: validDests.map(d => d.trim()),
      });
      onConnectorsChange?.();
      setTemplateModal(null);
      toast.success(t('templateInstanciateSuccess', { defaultValue: 'Canal compartilhado adicionado com sucesso!' }));
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.error || e.response?.data?.message || e.message;
      if (status === 409) {
        setTemplateError(t('templateConflict', { defaultValue: 'Conflito: ' }) + msg);
      } else if (status === 400) {
        setTemplateError(msg);
      } else {
        setTemplateError(msg);
      }
    } finally {
      setTemplateSaving(false);
    }
  };

  // ── edit instanced connector (allowedDestinations) ────────────────────────
  const [editInstanceModal, setEditInstanceModal] = useState(null); // connector object or null
  const [editInstanceDests, setEditInstanceDests] = useState(['']);
  const [editInstanceSaving, setEditInstanceSaving] = useState(false);
  const [editInstanceError, setEditInstanceError] = useState('');

  const openEditInstanceModal = (connector) => {
    setEditInstanceModal(connector);
    setEditInstanceDests(connector.allowedDestinations?.length > 0 ? [...connector.allowedDestinations] : ['']);
    setEditInstanceError('');
  };

  const handleEditInstanceSave = async () => {
    if (!editInstanceModal || !tenantId) return;
    const validDests = editInstanceDests.filter(d => d.trim());
    if (validDests.length === 0) {
      setEditInstanceError(t('templateNoDestination', { defaultValue: 'Informe ao menos um destinatário.' }));
      return;
    }
    const invalidDest = validDests.find(d => !validateDestination(d, editInstanceModal.type));
    if (invalidDest) {
      setEditInstanceError(t('templateInvalidDestination', { defaultValue: 'Destinatário inválido: ' }) + invalidDest);
      return;
    }
    setEditInstanceSaving(true);
    setEditInstanceError('');
    try {
      await api.put(`/connectors/${tenantId}/${editInstanceModal.connectorId}`, {
        allowedDestinations: validDests.map(d => d.trim()),
      });
      onConnectorsChange?.();
      setEditInstanceModal(null);
      toast.success(t('connectorUpdated', { defaultValue: 'Conector atualizado com sucesso!' }));
    } catch (e) {
      setEditInstanceError(e.response?.data?.error || e.response?.data?.message || e.message);
    } finally {
      setEditInstanceSaving(false);
    }
  };

  const enabledActions = formData.enabledActions || [];
  const handleToggleTool = async (toolName) => {
    const currentEnabled = enabledActions.includes(toolName);
    const newActions = toggleToolUtil(enabledActions, toolName, !currentEnabled);

    // Optimistically update local state
    setFormData(prev => ({ ...prev, enabledActions: newActions }));

    // If we have a tenantId and agentInstanceId, persist to backend
    if (tenantId && agentInstanceId) {
      try {
        await api.put(`/marketplace/tenants/${tenantId}/agents/${agentInstanceId}`, {
          enabledActions: newActions,
        });
      } catch (err) {
        // Revert on failure
        setFormData(prev => ({ ...prev, enabledActions }));
        toast.error(t('toolToggleError', { defaultValue: 'Erro ao atualizar ferramenta. Tente novamente.' }));
      }
    }
  };

  const canEdit = isConfiguring || isSallusAdmin;

  // Auth-required tools (tools that require OTP authentication before execution)
  const authRequiredTools = formData.authRequiredTools || [];
  const hasAuthConnector = agentConnectors.some(c => c.type === 'auth' && c.enabled !== false);

  const handleToggleAuthRequired = async (toolName) => {
    const isProtected = authRequiredTools.includes(toolName);
    const newList = isProtected
      ? authRequiredTools.filter(t => t !== toolName)
      : [...authRequiredTools, toolName];

    setFormData(prev => ({ ...prev, authRequiredTools: newList }));

    if (tenantId && agentInstanceId) {
      try {
        await api.put(`/marketplace/tenants/${tenantId}/agents/${agentInstanceId}`, {
          authRequiredTools: newList,
        });
      } catch (err) {
        setFormData(prev => ({ ...prev, authRequiredTools }));
        toast.error(t('authToggleError', { defaultValue: 'Erro ao atualizar proteção de autenticação.' }));
      }
    }
  };

  const createCredential = async () => {
    if (!credModalName.trim()) return;
    setCredSaving(true);
    try {
      await api.post(`/connectors/${tenantId}/credentials`, { type: credModalType, name: credModalName.trim() });
      onCredentialsChange?.();
      setCredModalType(null);
      setCredModalName('');
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
    finally { setCredSaving(false); }
  };

  const deleteCredential = async (credentialId) => {
    setRemovingConnectorId(credentialId);
    try {
      await api.delete(`/connectors/${tenantId}/credentials/${credentialId}`);
      onCredentialsChange?.();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
    finally { setRemovingConnectorId(null); }
  };

  const linkCredentialToConnector = async (connector, credentialId) => {
    try {
      await api.put(`/connectors/${tenantId}/${connector.connectorId}`, { credentialRef: credentialId || null });
      onConnectorsChange?.();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
  };

  const getGoogleAuthUrlForCredential = (credentialId) => {
    const token = sessionStorage.getItem('idToken') || localStorage.getItem('idToken') || '';
    return `${process.env.REACT_APP_API_URL}/connectors/${tenantId}/google/auth?token=${encodeURIComponent(token)}&credentialRef=${encodeURIComponent(credentialId)}`;
  };

  // Abas do formulário — sempre mostra Conectores/Ferramentas quando configurando um agente com tenantId
  const hasSections = !!tenantId && isConfiguring;
  const hasCredentials = isConfiguring && tenantId && agentConnectors.some(c => ['google_workspace','alexa'].includes(c.type));

  // Badges das abas
  const connectorsBadge = (() => {
    const allConns = agentConnectors || [];
    if (!allConns.length) return null;
    // Conectores com credenciais pendentes (fromTemplate sem token, ou google sem accessToken)
    const pending = allConns.filter(c =>
      (c.fromTemplate && !c.enabled) ||
      (c.type === 'google_workspace' && !c.accessToken && !c.sourceTemplateId) ||
      (c.type === 'microsoft_graph' && !c.accessToken && !c.sourceTemplateId)
    ).length;
    return pending > 0 ? { count: allConns.length, warn: pending } : { count: allConns.length, warn: 0 };
  })();

  // sharedBadge removed — shared connectors tab no longer exists (unified into Conectores tab)

  const credentialsBadge = (() => {
    if (!hasCredentials) return null;
    const pending = tenantCredentials.filter(c => !c.hasToken).length;
    return pending > 0 ? 'warn' : null;
  })();

  const tabs = [
    { id: 'identity', label: t('tabIdentity', { defaultValue: 'Identidade' }) },
    ...(hasSections ? [{ id: 'connectors', label: t('connectors', { defaultValue: 'Conectores' }), connectorsBadge }] : []),
    ...(hasSections ? [{ id: 'tools', label: t('tools', { defaultValue: 'Ferramentas' }) }] : []),
    ...(hasCredentials ? [{ id: 'credentials', label: t('credentials.tab', { defaultValue: 'Credenciais' }), credentialsBadge }] : []),
  ];

  return (
    <>
      {/* ── Tab bar ── */}
      {tabs.length > 1 && (
        <AgentTabBar>
          {tabs.map(tab => (
            <AgentTab key={tab.id} $active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
              {tab.label}
              {/* Aba Conectores: Número de ativos + aviso se há pendentes */}
              {tab.connectorsBadge && (
                <>
                  <AgentTabBadge>{tab.connectorsBadge.count}</AgentTabBadge>
                  {tab.connectorsBadge.warn > 0 && (
                    <AgentTabWarn title={t('connectorsPendingConfig', { count: tab.connectorsBadge.warn, defaultValue: '{{count}} conector(es) aguardando configuração' })}>⚠️</AgentTabWarn>
                  )}
                </>
              )}
              {/* Aba Credenciais: aviso se há credencial pendente */}
              {tab.credentialsBadge === 'warn' && (
                <AgentTabWarn title={t('credentialPendingAuth', { defaultValue: 'Credencial aguardando autorização' })}>⚠️</AgentTabWarn>
              )}
            </AgentTab>
          ))}
        </AgentTabBar>
      )}

      {/* ── Aba: Identidade ── */}
      {activeTab === 'identity' && (
        <IdentityCol>
          <FormGroup>
            <Label style={{ margin: 0 }}>{t('instanceName')}</Label>
            <Input value={formData.instanceName || ''} onChange={e => setFormData({ ...formData, instanceName: e.target.value })} placeholder={t('instanceNamePlaceholder')} />
          </FormGroup>
          {/* ── Link público — compacto, logo após o nome ── */}
          {isConfiguring && (
            <PublicLinkCompact>
              <PublicLinkCompactRow>
                <span style={{ fontSize: 11, color: theme.colors.textMuted, whiteSpace: 'nowrap' }}>
                  🔗 libbenagents.com/u/{formData.publicSlug || tenantId || '...'}/
                </span>
                <Input
                  value={formData.agentPublicSlug || ''}
                  onChange={e => setFormData({ ...formData, agentPublicSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40) })}
                  placeholder={formData.instanceName?.toLowerCase().replace(/\s+/g, '-') || 'recepcao'}
                  style={{ flex: 1, minWidth: 0 }}
                />
                {formData.agentPublicSlug && (
                  <PublicLinkCopyBtn
                    type="button"
                    title={t('copyLink', { defaultValue: 'Copiar link' })}
                    onClick={() => {
                      const slug = `${window.location.origin}/u/${formData.publicSlug || tenantId}/${formData.agentPublicSlug}`;
                      navigator.clipboard.writeText(slug);
                    }}
                  >
                    <Copy size={13} />
                  </PublicLinkCopyBtn>
                )}
                {formData.agentPublicSlug && (
                  <RemoveSlugBtn
                    type="button"
                    title={t('removePublicLink', { defaultValue: 'Remover link público' })}
                    onClick={() => setFormData({ ...formData, agentPublicSlug: '' })}
                  >
                    ✕
                  </RemoveSlugBtn>
                )}
              </PublicLinkCompactRow>
              <PublicLinkCompactHint>
                {t('publicLinkHint', { defaultValue: 'Qualquer pessoa com este link pode conversar com seu agente, sem precisar de conta.' })}
                {' '}
                <PublicLinkAdvancedToggle type="button" onClick={() => setPublicLinkAdvanced(v => !v)}>
                  {publicLinkAdvanced
                    ? t('publicLinkHideAdvanced', { defaultValue: 'Ocultar opções' })
                    : t('publicLinkShowAdvanced', { defaultValue: 'Mais opções' })}
                </PublicLinkAdvancedToggle>
              </PublicLinkCompactHint>

              {publicLinkAdvanced && (
                <PublicLinkAdvanced>
                  <FormGroup style={{ marginBottom: 0 }}>
                    <Label style={{ margin: 0 }}>
                      {t('publicSlugTenantLabel', { defaultValue: 'Seu identificador (vale para todos os agentes)' })}
                    </Label>
                    <PublicLinkInputRow>
                      <PublicLinkPrefix>libbenagents.com/u/</PublicLinkPrefix>
                      <Input
                        value={formData.publicSlug || tenantId || ''}
                        onChange={e => setFormData({ ...formData, publicSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40) })}
                        placeholder={tenantId || 'seu-nome'}
                        style={{ borderRadius: '0 6px 6px 0', borderLeft: 'none', flex: 1 }}
                      />
                    </PublicLinkInputRow>
                    <FieldHint>{t('publicSlugTenantHint', { defaultValue: 'Identifica você na URL. Definido uma vez, vale para todos os seus agentes.' })}</FieldHint>
                  </FormGroup>
                  <FormGroup style={{ marginBottom: 0 }}>
                    <Label style={{ margin: 0 }}>{t('welcomeMessage', { defaultValue: 'Mensagem de boas-vindas' })}</Label>
                    <Input
                      value={formData.welcomeMessage || ''}
                      onChange={e => setFormData({ ...formData, welcomeMessage: e.target.value })}
                      placeholder={t('welcomeMessagePlaceholder', { defaultValue: 'Olá! Como posso te ajudar hoje?' })}
                    />
                  </FormGroup>
                </PublicLinkAdvanced>
              )}
            </PublicLinkCompact>
          )}

          {isSallusAdmin && (
            <FormGroup style={{ width: 80 }}>
              <Label style={{ margin: 0 }}>{t('iconLabel', { defaultValue: 'Ícone' })}</Label>
              <Input value={formData.icon || '🤖'} onChange={e => setFormData({ ...formData, icon: e.target.value })} />
              <FieldHint>{t('iconHint', { defaultValue: 'Emoji ou' })} <code>si:notion</code></FieldHint>
            </FormGroup>
          )}

          {isSallusAdmin && !isConfiguring && (
            <>
              <FormGroup>
                <Label style={{ margin: 0 }}>Agent ID</Label>
                <Input
                  value={formData.agentId || ''}
                  onChange={e => setFormData({ ...formData, agentId: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })}
                  placeholder="ex: sofia"
                  style={{
                    borderColor: formData.agentId && formData.agentId.length < 2
                      ? theme.colors.danger
                      : formData.agentId ? theme.colors.success : theme.colors.border
                  }}
                />
                {formData.agentId && formData.agentId.length < 2 && (
                  <FieldHint style={{ color: theme.colors.danger }}>{t('agentIdMinChars', { defaultValue: 'Mínimo 2 caracteres' })}</FieldHint>
                )}
                {formData.agentId && formData.agentId.length >= 2 && (
                  <FieldHint style={{ color: theme.colors.success }}>{t('agentIdValid', { defaultValue: '✓ válido' })}</FieldHint>
                )}
              </FormGroup>
              <Flex gap={theme.spacing[3]}>
                <FormGroup style={{ flex: 1 }}>
                  <Label style={{ margin: 0 }}>{t('roleLabel', { defaultValue: 'Função' })}</Label>
                  <Input value={formData.role || ''} onChange={e => setFormData({ ...formData, role: e.target.value })} placeholder={t('rolePlaceholder', { defaultValue: 'ex: Recepcionista' })} />
                </FormGroup>
              </Flex>
              <FormGroup>
                <Label style={{ margin: 0 }}>{t('common:description')}</Label>
                <Textarea rows={2} value={formData.description || ''} onChange={e => setFormData({ ...formData, description: e.target.value })} />
              </FormGroup>
              <FormGroup>
                <LabelWithHelp>
                  <Label style={{ margin: 0 }}>{t('basePrompt', { defaultValue: 'Prompt Base' })} <RequiredMark>*</RequiredMark></Label>
                  <Popover content={t('basePromptTooltip', { defaultValue: 'Prompt obrigatório incluído em todas as instâncias. O tenant não pode removê-lo ou editá-lo.' })}><HelpCircle size={13} color={theme.colors.textMuted} /></Popover>
                </LabelWithHelp>
                <Textarea
                  rows={5}
                  value={formData.basePrompt || ''}
                  onChange={e => setFormData({ ...formData, basePrompt: e.target.value })}
                  placeholder={t('basePromptPlaceholder', { defaultValue: 'Instruções obrigatórias que sempre serão incluídas no agente...' })}
                  style={{ borderColor: !formData.basePrompt?.trim() ? theme.colors.danger : theme.colors.border }}
                />
                <CharCounter $warn={(formData.basePrompt || '').length > 3000}>
                  {(formData.basePrompt || '').length.toLocaleString()} {t('charactersLabel', { defaultValue: 'caracteres' })}
                  {(formData.basePrompt || '').length > 3000 && ' — ' + t('longPromptWarning', { defaultValue: 'prompts longos aumentam custo e latência' })}
                </CharCounter>
                {!formData.basePrompt?.trim() && <FieldHint style={{ color: theme.colors.danger }}>{t('requiredField', { defaultValue: 'Obrigatório' })}</FieldHint>}
              </FormGroup>
            </>
          )}

          {formData.model !== 'relay' && (
          <FormGroup>
            <LabelWithHelp>
              <Label style={{ margin: 0 }}>{t('systemPrompt')}</Label>
              <Popover content={t('tooltips.systemPrompt')}><HelpCircle size={13} color={theme.colors.textMuted} /></Popover>
            </LabelWithHelp>
            {isConfiguring && isSallusAdmin && (
              // Libben (sallus_admin) pode editar o Prompt Base mesmo ao configurar
              // um agente existente. O tenant vê apenas o bloco read-only abaixo.
              <FormGroup>
                <LabelWithHelp>
                  <Label style={{ margin: 0 }}>{t('basePrompt', { defaultValue: 'Prompt Base' })} <RequiredMark>*</RequiredMark></Label>
                  <Popover content={t('basePromptTooltipAdmin', { defaultValue: 'Prompt obrigatório incluído em todas as instâncias. Editável apenas pela equipe Libben; o tenant não pode alterá-lo.' })}><HelpCircle size={13} color={theme.colors.textMuted} /></Popover>
                </LabelWithHelp>
                <Textarea
                  rows={5}
                  value={formData.basePrompt || ''}
                  onChange={e => setFormData({ ...formData, basePrompt: e.target.value })}
                  placeholder={t('basePromptPlaceholder', { defaultValue: 'Instruções obrigatórias que sempre serão incluídas no agente...' })}
                  style={{ borderColor: !formData.basePrompt?.trim() ? theme.colors.danger : theme.colors.border }}
                />
                <CharCounter $warn={(formData.basePrompt || '').length > 3000}>
                  {(formData.basePrompt || '').length.toLocaleString()} {t('charactersLabel', { defaultValue: 'caracteres' })}
                  {(formData.basePrompt || '').length > 3000 && ' — ' + t('longPromptWarning', { defaultValue: 'prompts longos aumentam custo e latência' })}
                </CharCounter>
                {!formData.basePrompt?.trim() && <FieldHint style={{ color: theme.colors.danger }}>{t('requiredField', { defaultValue: 'Obrigatório' })}</FieldHint>}
              </FormGroup>
            )}
            {isConfiguring && !isSallusAdmin && formData.basePrompt && (
              <BasePromptCollapsible>
                <BasePromptToggle onClick={() => setBasePromptExpanded(v => !v)}>
                  <span>🔒 {t('basePromptReadOnly', { defaultValue: 'Prompt Base (somente leitura)' })}</span>
                  {basePromptExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </BasePromptToggle>
                {basePromptExpanded && (
                  <BasePromptText>{formData.basePrompt}</BasePromptText>
                )}
              </BasePromptCollapsible>
            )}
            <Textarea
              rows={8}
              value={formData.customPrompt || ''}
              onChange={e => setFormData({ ...formData, customPrompt: e.target.value })}
              placeholder={isConfiguring ? t('customPromptPlaceholderConfig', { defaultValue: 'Adicione instruções complementares ao agente...' }) : t('customPromptPlaceholder', { defaultValue: 'Você é...' })}
            />
            <CharCounter $warn={(formData.customPrompt || '').length > 3000}>
              {(formData.customPrompt || '').length.toLocaleString()} {t('charactersLabel', { defaultValue: 'caracteres' })}
              {(formData.customPrompt || '').length > 3000 && ' — ' + t('longPromptWarning', { defaultValue: 'prompts longos aumentam custo e latência' })}
            </CharCounter>
            {isConfiguring && !isSallusAdmin && formData.basePrompt && (
              <FieldHint>{t('basePromptAutoIncluded', { defaultValue: 'O Prompt Base é sempre incluído automaticamente e não pode ser removido.' })}</FieldHint>
            )}
          </FormGroup>
          )}

          <Flex gap={theme.spacing[3]} style={{ flexWrap: 'wrap' }}>
            <FormGroup style={{ flex: 1 }}>
              <LabelWithHelp>
                <Label style={{ margin: 0 }}>{t('model')}</Label>
                <Popover content={t('tooltips.model')}><HelpCircle size={13} color={theme.colors.textMuted} /></Popover>
              </LabelWithHelp>
              {isSallusAdmin
                ? <>
                    {modelsError && <FieldHint style={{ color: theme.colors.danger }}>{modelsError}</FieldHint>}
                    <Select
                      value={formData.model || ''}
                      onChange={e => setFormData({ ...formData, model: e.target.value })}
                      disabled={!!modelsError || modelsLoading}
                    >
                      <option value="relay">{t('modelRelay', { defaultValue: 'relay (sem LLM — ponte entre canais)' })}</option>
                      {modelsLoading && <option disabled>{t('loadingModels', { defaultValue: 'Carregando modelos...' })}</option>}
                      {availableModels.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </Select>
                  </>
                : <ReadOnly>{formData.model}{formData.creditsPerMessage != null && ` (${formData.creditsPerMessage} ${t('creditsPerMessage', { defaultValue: 'créditos/msg' })})`}</ReadOnly>
              }
            </FormGroup>
            {formData.model !== 'relay' && (
            <FormGroup style={{ width: 100, minWidth: 80 }}>
              <LabelWithHelp>
                <Label style={{ margin: 0 }}>{t('temperature')}</Label>
                <Popover content={t('tooltips.temperature')}><HelpCircle size={13} color={theme.colors.textMuted} /></Popover>
              </LabelWithHelp>
              {isSallusAdmin
                ? <Input type="number" step="0.1" min="0" max="1" value={formData.temperature ?? 0.7} onChange={e => setFormData({ ...formData, temperature: e.target.value })} />
                : <ReadOnly>{formData.temperature}</ReadOnly>
              }
            </FormGroup>
            )}
          </Flex>

          {isSallusAdmin && (
            <FormGroup>
              <Label style={{ margin: 0 }}>{t('audienceLabel', { defaultValue: 'Audience' })}</Label>
              <Select value={formData.audience || 'both'} onChange={e => setFormData({ ...formData, audience: e.target.value })}>
                <option value="both">{t('audienceBoth', { defaultValue: 'Todos (B2B + B2C)' })}</option>
                <option value="b2b">{t('audienceB2b', { defaultValue: 'Somente B2B' })}</option>
                <option value="b2c">{t('audienceB2c', { defaultValue: 'Somente B2C' })}</option>
              </Select>
            </FormGroup>
          )}

          {isSallusAdmin && (
            <Flex gap={theme.spacing[3]}>
              <FormGroup style={{ flex: 1 }}>
                <Label style={{ margin: 0 }}>{t('categoryLabel', { defaultValue: 'Categoria' })}</Label>
                <Select value={formData.category || 'geral'} onChange={e => setFormData({ ...formData, category: e.target.value })}>
                  {AGENT_CATEGORY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup style={{ flex: 2 }}>
                <Label style={{ margin: 0 }}>{t('tagsLabel', { defaultValue: 'Tags' })} <span style={{ fontWeight: 400, color: theme.colors.textMuted }}>{t('tagsCommaSeparated', { defaultValue: '(separadas por vírgula)' })}</span></Label>
                <Input
                  value={tagsText}
                  onChange={e => {
                    const raw = e.target.value;
                    setTagsText(raw); // preserva o texto cru (incl. a vírgula recém-digitada)
                    // Mantém formData.tags sempre sincronizado (sobrevive a "Salvar"
                    // sem blur), mas sem reconstruir o value a partir do array.
                    setFormData({ ...formData, tags: raw.split(',').map(t => t.trim()).filter(Boolean) });
                  }}
                  placeholder={t('tagsPlaceholder', { defaultValue: 'ex: agendamento, naja, saúde' })}
                />
              </FormGroup>
            </Flex>
          )}



          {isConfiguring && (
            <ToggleRow>
              <div>
                <ToggleLabel>{t('knowledgeBase', { defaultValue: 'Base de Conhecimento' })}</ToggleLabel>
                <ToggleDesc>{t('knowledgeBaseToggleDesc', { defaultValue: 'O agente consulta automaticamente os documentos carregados em Base de Conhecimento para enriquecer as respostas.' })}</ToggleDesc>
              </div>
              <ToggleSwitch
                $active={formData.useKnowledgeBase !== false}
                onClick={() => setFormData({ ...formData, useKnowledgeBase: formData.useKnowledgeBase === false ? true : false })}
              >
                <ToggleThumb $active={formData.useKnowledgeBase !== false} />
              </ToggleSwitch>
            </ToggleRow>
          )}

          {isConfiguring && (
            <ToggleRow>
              <div>
                <ToggleLabel>{t('promptCache', { defaultValue: 'Cache de prompt (Bedrock)' })}</ToggleLabel>
                <ToggleDesc>
                  {t('promptCacheDesc', { defaultValue: 'Reutiliza prompt e ferramentas em cache (TTL 1h) para reduzir custo de tokens em agentes de alto volume.' })}
                  {!isSallusAdmin && ' ' + t('promptCacheAdminOnly', { defaultValue: 'Somente a equipe Libben pode alterar esta configuração.' })}
                </ToggleDesc>
              </div>
              <ToggleSwitch
                $active={formData.usePromptCache === true}
                onClick={() => { if (isSallusAdmin) setFormData({ ...formData, usePromptCache: formData.usePromptCache !== true }); }}
                style={{ opacity: isSallusAdmin ? 1 : 0.5, cursor: isSallusAdmin ? 'pointer' : 'not-allowed' }}
              >
                <ToggleThumb $active={formData.usePromptCache === true} />
              </ToggleSwitch>
            </ToggleRow>
          )}

          {/* Limites — readonly para tenant */}
          {isConfiguring && (
            <>
              <SectionLabel>{t('limitSettings', { defaultValue: 'Configurações de Limite' })}</SectionLabel>
              <Flex gap={theme.spacing[3]}>
                <FormGroup style={{ flex: 1 }}>
                  <Label style={{ margin: 0 }}>{t('tokensPerSession', { defaultValue: 'Limite de tokens/sessão' })}</Label>
                  <ReadOnly>{formData.maxTokensPerSession || t('noLimit', { defaultValue: 'Sem limite' })}</ReadOnly>
                </FormGroup>
                <FormGroup style={{ flex: 1 }}>
                  <Label style={{ margin: 0 }}>{t('sessionTimeoutMin', { defaultValue: 'Timeout de sessão (min)' })}</Label>
                  {isSallusAdmin
                    ? <Input
                        type="number"
                        min="1"
                        value={formData.sessionTimeoutMinutes || ''}
                        onChange={e => setFormData({ ...formData, sessionTimeoutMinutes: e.target.value })}
                        placeholder={t('sessionTimeoutPlaceholder', { defaultValue: '30 (padrão)' })}
                      />
                    : <ReadOnly>{formData.sessionTimeoutMinutes || '30'}</ReadOnly>
                  }
                </FormGroup>
                <FormGroup style={{ flex: 1 }}>
                  <Label style={{ margin: 0 }}>{t('maxToolIterations', { defaultValue: 'Max iterações de tools' })}</Label>
                  {isSallusAdmin
                    ? <Input
                        type="number"
                        min="1"
                        max="30"
                        value={formData.maxIterations || ''}
                        onChange={e => setFormData({ ...formData, maxIterations: e.target.value })}
                        placeholder={t('maxIterationsPlaceholder', { defaultValue: '8 (padrão: 8 para tasks, 5 para chat)' })}
                      />
                    : <ReadOnly>{formData.maxIterations || '8'}</ReadOnly>
                  }
                </FormGroup>
                <FormGroup style={{ flex: 1 }}>
                  <LabelWithHelp>
                    <Label style={{ margin: 0 }}>{t('contextWindowMsgs', { defaultValue: 'Janela de contexto (msgs)' })}</Label>
                    <Popover content={t('contextWindowTooltip', { defaultValue: 'Quantas mensagens anteriores o agente lembra na conversa. Valores maiores aumentam o contexto, mas consomem mais tokens.' })}>
                      <HelpCircle size={13} color={theme.colors.textMuted} />
                    </Popover>
                  </LabelWithHelp>
                  {isSallusAdmin
                    ? <Input
                        type="number"
                        min="1"
                        max="50"
                        value={formData.contextWindowSize || ''}
                        onChange={e => setFormData({ ...formData, contextWindowSize: e.target.value })}
                        placeholder={t('contextWindowPlaceholder', { defaultValue: '10 (padrão)' })}
                      />
                    : <ReadOnly>{formData.contextWindowSize || '10'}</ReadOnly>
                  }
                </FormGroup>
              </Flex>
              {formData.sessionLimitMessage && (
                <FormGroup>
                  <Label style={{ margin: 0 }}>{t('sessionLimitMessageLabel', { defaultValue: 'Mensagem ao atingir limite' })}</Label>
                  <ReadOnly style={{ whiteSpace: 'normal' }}>{formData.sessionLimitMessage}</ReadOnly>
                </FormGroup>
              )}
            </>
          )}

          {/* Processamento de Mídia */}
          {isConfiguring && (
            <>
              <SectionLabel>{t('mediaProcessing', { defaultValue: 'Processamento de Mídia' })}</SectionLabel>
              <ToggleRow style={{ opacity: mediaFeatures.vision ? 1 : 0.5 }}>
                <div>
                  <ToggleLabel>{t('processImages', { defaultValue: 'Analisar Imagens' })}</ToggleLabel>
                  <ToggleDesc>
                    {mediaFeatures.vision
                      ? t('processImagesDesc', { defaultValue: 'Permite que o agente visualize e interprete imagens enviadas pelo cliente (usa visão multimodal).' })
                      : t('upgradeRequired', { defaultValue: '🔒 Disponível em planos superiores ou como add-on.' })}
                  </ToggleDesc>
                </div>
                <ToggleSwitch
                  $active={formData.mediaHandling?.processImages === true}
                  onClick={() => mediaFeatures.vision && setFormData({ ...formData, mediaHandling: { ...formData.mediaHandling, processImages: !formData.mediaHandling?.processImages } })}
                  style={{ cursor: mediaFeatures.vision ? 'pointer' : 'not-allowed' }}
                >
                  <ToggleThumb $active={formData.mediaHandling?.processImages === true} />
                </ToggleSwitch>
              </ToggleRow>
              {formData.mediaHandling?.processImages !== true && (
                <FormGroup style={{ marginTop: `-${theme.spacing[2]}` }}>
                  <Label style={{ margin: 0 }}>{t('imageUnsupportedMessage', { defaultValue: 'Resposta quando não analisar imagens' })}</Label>
                  <Textarea
                    rows={2}
                    value={formData.mediaHandling?.imageUnsupportedMessage || ''}
                    onChange={e => setFormData({ ...formData, mediaHandling: { ...formData.mediaHandling, imageUnsupportedMessage: e.target.value } })}
                    placeholder={t('imageUnsupportedPlaceholder', { defaultValue: 'Recebi sua imagem mas não consigo analisá-la. Pode descrever?' })}
                  />
                  <FieldHint>{t('unsupportedMessageHint', { defaultValue: 'Enviada ao cliente quando este tipo de mídia não é interpretado. O arquivo ainda pode ser anexado/arquivado se você pedir ao agente.' })}</FieldHint>
                </FormGroup>
              )}
              <ToggleRow style={{ opacity: mediaFeatures.audio ? 1 : 0.5 }}>
                <div>
                  <ToggleLabel>{t('processAudio', { defaultValue: 'Transcrever Áudio' })}</ToggleLabel>
                  <ToggleDesc>
                    {mediaFeatures.audio
                      ? t('processAudioDesc', { defaultValue: 'Transcreve áudios recebidos (WhatsApp, etc.) e responde com base no conteúdo falado.' })
                      : t('upgradeRequired', { defaultValue: '🔒 Disponível em planos superiores ou como add-on.' })}
                  </ToggleDesc>
                </div>
                <ToggleSwitch
                  $active={formData.mediaHandling?.processAudio === true}
                  onClick={() => mediaFeatures.audio && setFormData({ ...formData, mediaHandling: { ...formData.mediaHandling, processAudio: !formData.mediaHandling?.processAudio } })}
                  style={{ cursor: mediaFeatures.audio ? 'pointer' : 'not-allowed' }}
                >
                  <ToggleThumb $active={formData.mediaHandling?.processAudio === true} />
                </ToggleSwitch>
              </ToggleRow>
              {formData.mediaHandling?.processAudio !== true && (
                <FormGroup style={{ marginTop: `-${theme.spacing[2]}` }}>
                  <Label style={{ margin: 0 }}>{t('audioUnsupportedMessage', { defaultValue: 'Resposta quando não transcrever áudio' })}</Label>
                  <Textarea
                    rows={2}
                    value={formData.mediaHandling?.audioUnsupportedMessage || ''}
                    onChange={e => setFormData({ ...formData, mediaHandling: { ...formData.mediaHandling, audioUnsupportedMessage: e.target.value } })}
                    placeholder={t('audioUnsupportedPlaceholder', { defaultValue: 'Recebi seu áudio mas não consigo ouvi-lo. Pode digitar?' })}
                  />
                  <FieldHint>{t('unsupportedMessageHint', { defaultValue: 'Enviada ao cliente quando este tipo de mídia não é interpretado. O arquivo ainda pode ser anexado/arquivado se você pedir ao agente.' })}</FieldHint>
                </FormGroup>
              )}
              <ToggleRow style={{ opacity: mediaFeatures.documents ? 1 : 0.5 }}>
                <div>
                  <ToggleLabel>{t('processDocuments', { defaultValue: 'Processar Documentos' })}</ToggleLabel>
                  <ToggleDesc>
                    {mediaFeatures.documents
                      ? t('processDocumentsDesc', { defaultValue: 'Extrai texto de PDFs e documentos enviados pelo cliente para uso na conversa.' })
                      : t('upgradeRequired', { defaultValue: '🔒 Disponível em planos superiores ou como add-on.' })}
                  </ToggleDesc>
                </div>
                <ToggleSwitch
                  $active={formData.mediaHandling?.processDocuments === true}
                  onClick={() => mediaFeatures.documents && setFormData({ ...formData, mediaHandling: { ...formData.mediaHandling, processDocuments: !formData.mediaHandling?.processDocuments } })}
                  style={{ cursor: mediaFeatures.documents ? 'pointer' : 'not-allowed' }}
                >
                  <ToggleThumb $active={formData.mediaHandling?.processDocuments === true} />
                </ToggleSwitch>
              </ToggleRow>
              {formData.mediaHandling?.processDocuments !== true && (
                <FormGroup style={{ marginTop: `-${theme.spacing[2]}` }}>
                  <Label style={{ margin: 0 }}>{t('documentUnsupportedMessage', { defaultValue: 'Resposta quando não processar documentos' })}</Label>
                  <Textarea
                    rows={2}
                    value={formData.mediaHandling?.documentUnsupportedMessage || ''}
                    onChange={e => setFormData({ ...formData, mediaHandling: { ...formData.mediaHandling, documentUnsupportedMessage: e.target.value } })}
                    placeholder={t('documentUnsupportedPlaceholder', { defaultValue: 'Recebi o documento mas não consigo processá-lo. Pode resumir o conteúdo?' })}
                  />
                  <FieldHint>{t('unsupportedMessageHint', { defaultValue: 'Enviada ao cliente quando este tipo de mídia não é interpretado. O arquivo ainda pode ser anexado/arquivado se você pedir ao agente.' })}</FieldHint>
                </FormGroup>
              )}
            </>
          )}
        </IdentityCol>
      )}

      {/* ── Aba: Conectores ── */}
      {activeTab === 'connectors' && (() => {
        // Filter: exclude system connectors; show all audience types for admin/B2B
        const filteredConnectorTypes = allConnectorTypes.filter(ct =>
          !ct.system && ct.audience !== 'system'
        );
        const installedTypes = filteredConnectorTypes.filter(ct =>
          tenantId
            ? agentConnectors.some(c => c.type === ct.type && !c.sourceTemplateId)
            : (formData.connectors || []).some(c => c.type === ct.type)
        );
        const availableTypes = filteredConnectorTypes.filter(ct =>
          tenantId
            ? !agentConnectors.some(c => c.type === ct.type && !c.sourceTemplateId)
            : !(formData.connectors || []).some(c => c.type === ct.type)
        );
        const renderConnectorCard = (ct, connectorOverride) => {
                const existing = connectorOverride || (tenantId ? agentConnectors.find(c => c.type === ct.type) : (formData.connectors || []).find(c => c.type === ct.type));
                const isInstanced = !!existing?.sourceTemplateId;
                const tools = ct.capabilities?.tools || [];
                const isExpanded = expandedTools[ct.type + (existing?.connectorId || '')];
                const ctDisabled = ct.enabled === false;
                const webhookUrl = existing ? getWebhookUrl(ct, existing) : null;
                const hasDetail = existing && (webhookUrl || ct.type === 'google_workspace' || ct.type === 'alexa' || tools.length > 0);
                const expandKey = ct.type + (existing?.connectorId || '');

                // Badge for instanced connectors (shared channel)
                const getSharedBadgeLabel = () => {
                  if (!isInstanced) return null;
                  const type = existing.type || ct.type;
                  const sharedLabel = t('shared', { defaultValue: 'Compartilhado' });
                  if (type === 'whatsapp') return '📱 ' + sharedLabel;
                  if (type === 'email') return '📧 ' + sharedLabel;
                  if (type === 'telegram') return '✈️ ' + sharedLabel;
                  if (type === 'sms') return '💬 ' + sharedLabel;
                  return '🔗 ' + sharedLabel;
                };

                return (
                  <ConnCard key={existing?.connectorId || ct.type} $linked={!!existing} style={{ opacity: ctDisabled ? 0.45 : 1 }}>
                    {/* ── Linha compacta ── */}
                    <ConnCardMain
                      onClick={() => canEdit && !ctDisabled && (isInstanced ? openEditInstanceModal(existing) : openCtModal(ct))}
                      style={{ cursor: ctDisabled ? 'not-allowed' : 'pointer' }}
                    >
                      <ConnIcon><BrandIcon icon={r(ct.icon) || '🔌'} size={20} /></ConnIcon>
                      <ConnInfo>
                        <ConnName>{existing?.name || r(ct.name)}</ConnName>
                        {existing && !ctDisabled && (
                          <ConnSubtitle>
                            {isInstanced && existing.displayPhone && <span>{existing.displayPhone}</span>}
                            {isInstanced && existing.displayEmail && <span>{existing.displayEmail}</span>}
                            {!isInstanced && ct.type === 'google_workspace' && (existing.accessToken ? '✅ ' + t('authorized', { defaultValue: 'Autorizado' }) : '⚠️ ' + t('pendingAuthorization', { defaultValue: 'Pendente autorização' }))}
                            {!isInstanced && webhookUrl && <span style={{ fontFamily: 'monospace', fontSize: 10 }}>{webhookUrl.replace(/^https?:\/\//, '').slice(0, 40)}...</span>}
                            {!isInstanced && !webhookUrl && ct.type !== 'google_workspace' && ct.type !== 'alexa' && tools.length > 0 && (
                              <span>{tools.length} {tools.length === 1 ? t('functionSingular', { defaultValue: 'função' }) : t('functionPlural', { defaultValue: 'funções' })}</span>
                            )}
                          </ConnSubtitle>
                        )}
                      </ConnInfo>
                      <ConnRight>
                        {ctDisabled
                          ? <Badge variant="default">{t('unavailable', { defaultValue: 'Indisponível' })}</Badge>
                          : existing
                            ? (
                              <>
                                {isInstanced && <Badge variant="info" style={{ fontSize: '9px' }}>{getSharedBadgeLabel()}</Badge>}
                                <Badge variant="success">✓ {t('activeBadge', { defaultValue: 'Ativo' })}</Badge>
                                {!isInstanced && (ct.configFields || []).map(field =>
                                  field.type === 'boolean' && field.badgeWhenFalse && existing[field.name] === false
                                    ? <Badge key={field.name} variant="warning" style={{ fontSize: '9px' }}>{r(field.badgeWhenFalse)}</Badge>
                                    : null
                                )}
                              </>
                            )
                            : <Badge variant="default">+ {t('addBadge', { defaultValue: 'Adicionar' })}</Badge>
                        }
                        {existing && canEdit && (
                          <RemoveBtn
                            onClick={e => { e.stopPropagation(); handleCtRemove(existing); }}
                            disabled={removingConnectorId === existing.connectorId}
                            aria-label={t('removeConnector', { defaultValue: 'Remover conector' })}
                          >
                            {removingConnectorId === existing.connectorId ? '…' : '✕'}
                          </RemoveBtn>
                        )}
                        {hasDetail && !isInstanced && (
                          <ExpandBtn
                            onClick={e => { e.stopPropagation(); setExpandedTools(p => ({ ...p, [expandKey]: !p[expandKey] })); }}
                            aria-label={isExpanded ? t('collapse', { defaultValue: 'Recolher' }) : t('expand', { defaultValue: 'Expandir' })}
                          >
                            {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                          </ExpandBtn>
                        )}
                      </ConnRight>
                    </ConnCardMain>

                    {/* ── Detalhe expandido ── */}
                    {isExpanded && hasDetail && (
                      <ConnDetail>
                        {ct.capabilities?.outbound?.type === 'webchat' && existing && (() => {
                          const mode = webchatMode[existing.connectorId] || 'button';
                          const MODE_OPTIONS = [
                            { key: 'button',   label: t('webchatMode.button.label', { defaultValue: 'Botão flutuante' }), hint: t('webchatMode.button.hint', { defaultValue: 'Botão no canto que abre o chat (padrão).' }) },
                            { key: 'floating', label: t('webchatMode.floating.label', { defaultValue: 'Janela flutuante' }), hint: t('webchatMode.floating.hint', { defaultValue: 'Chat já aberto no canto, sem botão.' }) },
                            { key: 'inline',   label: t('webchatMode.inline.label', { defaultValue: 'Embutido na página' }), hint: t('webchatMode.inline.hint', { defaultValue: 'Chat dentro de um elemento do seu site.' }) },
                          ];
                          // Linhas de config adicionais conforme o modo escolhido.
                          const extraCfg =
                            mode === 'floating' ? `,\n    hideButton: true` :
                            mode === 'inline'   ? `,\n    target: '#libben-chat'` : '';
                          const snippet =
                            `<script>\n  window.LibbenChat = {\n    tenantId: '${tenantId}',\n    connectorId: '${existing.connectorId}',\n    apiUrl: '${process.env.REACT_APP_API_URL}'${extraCfg}\n  };\n</script>\n<script src="${window.location.origin}/webchat.js" async></script>` +
                            (mode === 'inline'
                              ? `\n\n<!-- Coloque este elemento onde o chat deve aparecer: -->\n<div id="libben-chat" style="width:380px;height:520px"></div>`
                              : '');
                          const copied = copiedWebhook === snippet;
                          return (
                            <WebhookRow onClick={e => e.stopPropagation()} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                              <Flex align="center" gap="6px">
                                <HelpCircle size={12} color={theme.colors.primary} />
                                <WebhookLabel style={{ color: theme.colors.primary }}>{t('webchatHowToAdd', { defaultValue: 'Como adicionar ao seu site' })}</WebhookLabel>
                              </Flex>
                              <Flex align="center" gap="6px" style={{ flexWrap: 'wrap' }}>
                                {MODE_OPTIONS.map(opt => (
                                  <button
                                    key={opt.key}
                                    type="button"
                                    title={opt.hint}
                                    onClick={e => { e.stopPropagation(); setWebchatMode(m => ({ ...m, [existing.connectorId]: opt.key })); }}
                                    style={{
                                      fontSize: 10, padding: '4px 8px', borderRadius: 6, cursor: 'pointer',
                                      border: `1px solid ${mode === opt.key ? theme.colors.primary : theme.colors.border}`,
                                      background: mode === opt.key ? theme.colors.primary : 'transparent',
                                      color: mode === opt.key ? '#fff' : theme.colors.textMuted,
                                    }}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </Flex>
                              <span style={{ fontSize: 10, color: theme.colors.textMuted }}>
                                {MODE_OPTIONS.find(o => o.key === mode)?.hint}
                              </span>
                              <WebhookUrl style={{ whiteSpace: 'pre', fontSize: 10, width: '100%' }}>{snippet}</WebhookUrl>
                              <WebhookCopy onClick={e => copyWebhook(e, snippet)} title={t('copyCode', { defaultValue: 'Copiar código' })}>
                                {copied ? <Check size={12} color={theme.colors.success} /> : <Copy size={12} />}
                                <span style={{ fontSize: 10, marginLeft: 4 }}>{copied ? t('copied', { defaultValue: 'Copiado!' }) : t('copy', { defaultValue: 'Copiar' })}</span>
                              </WebhookCopy>
                            </WebhookRow>
                          );
                        })()}

                        {ct.type === 'google_workspace' && existing && canEdit && (() => {
                          const hasToken = existing.accessToken;
                          return (
                            <WebhookRow onClick={e => e.stopPropagation()}>
                              <WebhookLabel>{hasToken ? '✅ ' + t('authorized', { defaultValue: 'Autorizado' }) : '⚠️ ' + t('notAuthorized', { defaultValue: 'Não autorizado' })}</WebhookLabel>
                              <OAuthBtn $authorized={!!hasToken} onClick={e => { e.stopPropagation(); window.location.href = getGoogleAuthUrl(); }}>
                                {hasToken ? t('reauthorizeGoogle', { defaultValue: 'Reutorizar Google' }) : t('connectWithGoogle', { defaultValue: 'Conectar com Google' })}
                              </OAuthBtn>
                            </WebhookRow>
                          );
                        })()}

                        {ct.type === 'alexa' && existing && canEdit && (
                          <AlexaActivation tenantId={tenantId} connector={existing} />
                        )}

                        {webhookUrl && (() => {
                          const copied = copiedWebhook === webhookUrl;
                          return (
                            <WebhookRow onClick={e => e.stopPropagation()}>
                              <WebhookLabel>Webhook URL</WebhookLabel>
                              <WebhookUrl>{webhookUrl}</WebhookUrl>
                              <WebhookCopy onClick={e => copyWebhook(e, webhookUrl)} title={t('copy', { defaultValue: 'Copiar' })}>
                                {copied ? <Check size={12} color={theme.colors.success} /> : <Copy size={12} />}
                              </WebhookCopy>
                            </WebhookRow>
                          );
                        })()}

                        {tools.length > 0 && (
                          <ToolsPanel>
                            {tools.map(tool => {
                              const active = enabledActions.includes(tool.name);
                              return (
                                <ToolRow
                                  key={tool.name}
                                  $active={active}
                                  onClick={() => existing && canEdit && handleToggleTool(tool.name)}
                                  style={{ cursor: existing && canEdit ? 'pointer' : 'default', opacity: existing ? 1 : 0.45 }}
                                >
                                  <ToolCheck $active={active}>{active ? '✓' : ''}</ToolCheck>
                                  <ToolInfo>
                                    <ToolName>{tool.name}</ToolName>
                                    <ToolDesc>{r(tool.description) || tool.description}</ToolDesc>
                                  </ToolInfo>
                                </ToolRow>
                              );
                            })}
                            {!existing && <ToolsHint>{t('linkConnectorToEnableTools', { defaultValue: 'Associe o conector para habilitar as funções' })}</ToolsHint>}
                          </ToolsPanel>
                        )}
                      </ConnDetail>
                    )}
                  </ConnCard>
                );
              };
              return (
                <ConnectorsTab>
                  {filteredConnectorTypes.length > 0 || (tenantId && agentConnectors.some(c => c.sourceTemplateId)) ? (
                    <>
                      {/* Instanced connectors (from templates) — shown first */}
                      {tenantId && (() => {
                        const instancedConnectors = agentConnectors.filter(c => c.sourceTemplateId);
                        if (instancedConnectors.length === 0) return null;
                        return (
                          <ConnectorList>
                            {instancedConnectors.map(conn => {
                              const ct = allConnectorTypes.find(t => t.type === conn.type) || { type: conn.type, name: conn.name, icon: conn.icon, capabilities: {} };
                              return renderConnectorCard(ct, conn);
                            })}
                          </ConnectorList>
                        );
                      })()}

                      {/* Installed regular connectors */}
                      {installedTypes.length > 0 && (
                        <ConnectorList>
                          {installedTypes.map(ct => renderConnectorCard(ct))}
                        </ConnectorList>
                      )}
                      {/* Available (uninstalled) connector types */}
                      {availableTypes.filter(ct => ct.enabled !== false).length > 0 && (
                        <>
                          <ConnSectionLabel>
                            {t('availableIntegrations', { defaultValue: 'Integrações disponíveis' })}
                          </ConnSectionLabel>
                          <ConnectorList>
                            {availableTypes.filter(ct => ct.enabled !== false).map(ct => renderConnectorCard(ct))}
                          </ConnectorList>
                        </>
                      )}

                      {/* Available templates for instanciation */}
                      {tenantId && (() => {
                        // Filter out templates already instanced for this agent
                        const instancedTemplateIds = agentConnectors
                          .filter(c => c.sourceTemplateId)
                          .map(c => c.sourceTemplateId);
                        const availableTemplates = templates.filter(
                          tpl => tpl.enabled !== false && !instancedTemplateIds.includes(tpl.templateId)
                        );
                        if (availableTemplates.length === 0) return null;

                        // Group templates by type
                        const grouped = {};
                        availableTemplates.forEach(tpl => {
                          const type = tpl.type || 'other';
                          if (!grouped[type]) grouped[type] = [];
                          grouped[type].push(tpl);
                        });

                        const typeLabels = {
                          whatsapp: 'WhatsApp',
                          email: 'Email',
                          telegram: 'Telegram',
                          sms: 'SMS',
                        };

                        return (
                          <>
                            <ConnSectionLabel>
                              {t('availableSharedChannels', { defaultValue: 'Canais compartilhados disponíveis' })}
                            </ConnSectionLabel>
                            {Object.entries(grouped).map(([type, tpls]) => (
                              <TemplateGroup key={type}>
                                <TemplateGroupLabel>{typeLabels[type] || type}</TemplateGroupLabel>
                                <TemplateCardList>
                                  {tpls.map(tpl => (
                                    <TemplateCard key={tpl.templateId}>
                                      <TemplateCardIcon>
                                        <BrandIcon icon={tpl.icon || '🔌'} size={22} />
                                      </TemplateCardIcon>
                                      <TemplateCardInfo>
                                        <TemplateCardName>{tpl.name}</TemplateCardName>
                                        {tpl.description && (
                                          <TemplateCardDesc>{tpl.description}</TemplateCardDesc>
                                        )}
                                        {tpl.displayPhone && (
                                          <TemplateCardMeta>{tpl.displayPhone}</TemplateCardMeta>
                                        )}
                                        {tpl.displayEmail && (
                                          <TemplateCardMeta>{tpl.displayEmail}</TemplateCardMeta>
                                        )}
                                      </TemplateCardInfo>
                                      <TemplateAddBtn
                                        onClick={() => openTemplateModal(tpl)}
                                        disabled={!canEdit}
                                      >
                                        {t('addTemplate', { defaultValue: 'Adicionar' })}
                                      </TemplateAddBtn>
                                    </TemplateCard>
                                  ))}
                                </TemplateCardList>
                              </TemplateGroup>
                            ))}
                          </>
                        );
                      })()}
                    </>
                  ) : (
                    <ToolsEmptyState>
                      <Zap size={32} strokeWidth={1} color={theme.colors.textMuted} />
                      <ToolsEmptyText>
                        {t('noConnectorTypes', { defaultValue: 'Nenhuma integração disponível.' })}
                      </ToolsEmptyText>
                    </ToolsEmptyState>
                  )}
                </ConnectorsTab>
              );
            })()}

      {/* ── Aba: Ferramentas (Tools) ── */}
      {activeTab === 'tools' && (() => {
        const toolGroups = getDisplayableTools(enabledActions, agentConnectors, allConnectorTypes);
        const hasTools = toolGroups.some(g => g.tools.length > 0);
        return (
          <ConnectorsTab>
            {!hasTools ? (
              <ToolsEmptyState>
                <Zap size={32} strokeWidth={1} color={theme.colors.textMuted} />
                <ToolsEmptyText>
                  {t('noToolsAvailable', { defaultValue: 'Nenhuma ferramenta disponível. Adicione conectores na aba "Conectores" para habilitar ferramentas.' })}
                </ToolsEmptyText>
              </ToolsEmptyState>
            ) : (
              toolGroups.map(group => {
                const ct = group.connectorType;
                const conn = group.connector;
                return (
                  <ToolGroupCard key={conn.connectorId || ct.type}>
                    <ToolGroupHeader>
                      <BrandIcon icon={r(ct.icon) || '🔌'} size={18} />
                      <ToolGroupName>{r(ct.name) || ct.type}</ToolGroupName>
                      <ToolGroupCount>
                        {group.tools.filter(t => t.enabled).length}/{group.tools.length}
                      </ToolGroupCount>
                      {canEdit && (
                        <RemoveBtn
                          onClick={() => handleCtRemove(conn)}
                          disabled={removingConnectorId === conn.connectorId}
                          aria-label={t('removeConnector', { defaultValue: 'Remover conector' })}
                          title={t('removeConnector', { defaultValue: 'Remover conector' })}
                        >
                          {removingConnectorId === conn.connectorId ? '…' : '✕'}
                        </RemoveBtn>
                      )}
                    </ToolGroupHeader>
                    <ToolGroupList>
                      {group.tools.map(tool => (
                        <ToolToggleRow key={tool.name}>
                          <ToolToggleInfo>
                            <ToolToggleName>
                              {tool.name}
                              {hasAuthConnector && authRequiredTools.includes(tool.name) && (
                                <AuthBadge title={t('requiresOtpAuth', { defaultValue: 'Requer autenticação OTP' })}>🔐</AuthBadge>
                              )}
                            </ToolToggleName>
                            {tool.description && <ToolToggleDesc>{r(tool.description) || tool.description}</ToolToggleDesc>}
                            {tool.output_fields?.length > 0 && (
                              <ToolOutputFields>
                                <ToolOutputTitle>{t('returnsLabel', { defaultValue: 'Retorna:' })}</ToolOutputTitle>
                                {tool.output_fields.map(f => (
                                  <ToolOutputField key={f.name}><code>{f.name}</code> {f.description && `— ${f.description}`}</ToolOutputField>
                                ))}
                              </ToolOutputFields>
                            )}
                          </ToolToggleInfo>
                          <ToolToggleActions>
                            {hasAuthConnector && tool.enabled && !['request_authentication', 'verify_auth_code', 'invalidate_authentication', 'check_authentication'].includes(tool.name) && (
                              <AuthLockBtn
                                $active={authRequiredTools.includes(tool.name)}
                                onClick={() => canEdit && handleToggleAuthRequired(tool.name)}
                                title={authRequiredTools.includes(tool.name) ? t('removeAuthProtection', { defaultValue: 'Remover proteção de autenticação' }) : t('requireOtpAuth', { defaultValue: 'Exigir autenticação OTP' })}
                                style={{ cursor: canEdit ? 'pointer' : 'default', opacity: canEdit ? 1 : 0.5 }}
                              >
                                <Lock size={14} />
                              </AuthLockBtn>
                            )}
                            <ToggleSwitch
                              $active={tool.enabled}
                              onClick={() => canEdit && handleToggleTool(tool.name)}
                              style={{ cursor: canEdit ? 'pointer' : 'default', opacity: canEdit ? 1 : 0.5 }}
                              role="switch"
                              aria-checked={tool.enabled}
                              aria-label={tool.name}
                            >
                              <ToggleThumb $active={tool.enabled} />
                            </ToggleSwitch>
                          </ToolToggleActions>
                        </ToolToggleRow>
                      ))}
                    </ToolGroupList>
                  </ToolGroupCard>
                );
              })
            )}
          </ConnectorsTab>
        );
      })()}

      {/* ── Aba: Credenciais ── */}
      {activeTab === 'credentials' && isConfiguring && tenantId && (() => {
        const credTypes = ['google_workspace', 'alexa'];
        const relevantConnectors = agentConnectors.filter(c => credTypes.includes(c.type));
        if (relevantConnectors.length === 0) return null;
        return (
          <ConnectorsTab>
            <FieldHint style={{ marginBottom: theme.spacing[3] }}>
              {t('credentials.reuseHint', { defaultValue: 'Reutilize uma autorização já existente em múltiplos conectores do mesmo tenant.' })}
            </FieldHint>
            <ConnectorGrid2Col>
              {relevantConnectors.map(connector => {
                const creds = tenantCredentials.filter(c => c.type === connector.type);
                const current = connector.credentialRef;
                return (
                  <ConnCard key={connector.connectorId} $linked={!!current}>
                    <ConnCardMain style={{ cursor: 'default' }}>
                      <ConnIcon><BrandIcon icon={connector.type === 'google_workspace' ? 'si:google' : 'si:amazon'} size={22} /></ConnIcon>
                      <ConnInfo>
                        <ConnName>{connector.name}</ConnName>
                        <ConnType>{connector.type}</ConnType>
                      </ConnInfo>
                    </ConnCardMain>
                    <WebhookRow onClick={e => e.stopPropagation()} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
                      <Flex style={{ alignItems: 'center', gap: 6 }}>
                        <WebhookLabel style={{ flex: 1 }}>{t('credentials.label', { defaultValue: 'Credencial' })}</WebhookLabel>
                        <OAuthBtn onClick={() => { setCredModalType(connector.type); setCredModalName(''); }}>
                          + {t('credentials.new', { defaultValue: 'Nova' })}
                        </OAuthBtn>
                      </Flex>
                      <select
                        style={{ fontSize: 11, padding: '3px 6px', borderRadius: 4, border: `1px solid ${theme.colors.border}`, width: '100%' }}
                        value={current || ''}
                        onChange={e => linkCredentialToConnector(connector, e.target.value || null)}
                      >
                        <option value="">{t('credentials.ownNotShared', { defaultValue: 'Própria (não compartilhada)' })}</option>
                        {creds.map(c => (
                          <option key={c.credentialId} value={c.credentialId}>
                            {c.name} {c.hasToken ? '✅' : '⚠️'}
                          </option>
                        ))}
                      </select>
                      {current && (() => {
                        const cred = tenantCredentials.find(c => c.credentialId === current);
                        if (!cred) return null;
                        return (
                          <Flex style={{ alignItems: 'center', gap: 6, marginTop: 2 }}>
                            <WebhookLabel style={{ flex: 1 }}>
                              {cred.hasToken ? '✅ ' + t('credentials.authorizedFem', { defaultValue: 'Autorizada' }) : '⚠️ ' + t('credentials.pending', { defaultValue: 'Pendente' })}
                            </WebhookLabel>
                            {connector.type === 'google_workspace' && (
                              <OAuthBtn
                                $authorized={cred.hasToken}
                                onClick={() => { window.location.href = getGoogleAuthUrlForCredential(current); }}
                              >
                                {cred.hasToken ? t('reauthorize', { defaultValue: 'Reutorizar' }) : t('authorizeGoogle', { defaultValue: 'Autorizar Google' })}
                              </OAuthBtn>
                            )}
                            {connector.type === 'alexa' && (
                              <AlexaActivation tenantId={tenantId} connector={{ ...connector, credentialRef: current }} />
                            )}
                            <RemoveBtn
                              title={t('credentials.remove', { defaultValue: 'Remover credencial' })}
                              onClick={() => deleteCredential(current)}
                              style={{ background: theme.colors.danger }}
                              disabled={removingConnectorId === current}
                            >
                              {removingConnectorId === current ? '…' : '✕'}
                            </RemoveBtn>
                          </Flex>
                        );
                      })()}
                    </WebhookRow>
                  </ConnCard>
                );
              })}
            </ConnectorGrid2Col>
          </ConnectorsTab>
        );
      })()}

      {/* ── Modais ── */}

      {/* Modal instanciação de template */}
      {templateModal && (
        <ModalOverlay onClick={() => setTemplateModal(null)}>
          <ModalBox maxWidth="480px" onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <BrandIcon icon={templateModal.icon || '🔌'} size={20} style={{ marginRight: 8 }} />
                {templateModal.name}
              </ModalTitle>
              <Button variant="ghost" size="sm" onClick={() => setTemplateModal(null)}>✕</Button>
            </ModalHeader>
            <ModalBody>
              {templateModal.description && (
                <TemplateModalDesc>{templateModal.description}</TemplateModalDesc>
              )}
              {templateModal.displayPhone && (
                <TemplateModalMeta>📱 {templateModal.displayPhone}</TemplateModalMeta>
              )}
              {templateModal.displayEmail && (
                <TemplateModalMeta>📧 {templateModal.displayEmail}</TemplateModalMeta>
              )}
              <FormGroup style={{ marginTop: theme.spacing[3] }}>
                <Label style={{ margin: 0 }}>{getTemplateInputConfig(templateModal).label}</Label>
                <FieldHint style={{ marginBottom: theme.spacing[2] }}>
                  {templateModal.type === 'email'
                    ? t('templateDestHintEmail', { defaultValue: 'Informe o(s) endereço(s) de email autorizado(s) para receber mensagens neste canal.' })
                    : templateModal.type === 'telegram'
                      ? t('templateDestHintTelegram', { defaultValue: 'Informe o Chat ID do Telegram autorizado para este canal.' })
                      : t('templateDestHintPhone', { defaultValue: 'Informe o número de telefone autorizado para envio/recebimento neste canal.' })
                  }
                </FieldHint>
                {templateDestinations.map((dest, idx) => (
                  <TemplateDestRow key={idx}>
                    <Input
                      type={getTemplateInputConfig(templateModal).type}
                      value={dest}
                      onChange={e => {
                        const updated = [...templateDestinations];
                        updated[idx] = e.target.value;
                        setTemplateDestinations(updated);
                      }}
                      placeholder={getTemplateInputConfig(templateModal).placeholder}
                      autoFocus={idx === 0}
                      style={{
                        flex: 1,
                        borderColor: dest && !validateDestination(dest, templateModal.type)
                          ? theme.colors.danger
                          : undefined,
                      }}
                    />
                    {templateDestinations.length > 1 && (
                      <TemplateDestRemoveBtn
                        type="button"
                        onClick={() => setTemplateDestinations(templateDestinations.filter((_, i) => i !== idx))}
                        title={t('remove', { ns: 'common', defaultValue: 'Remover' })}
                      >
                        ✕
                      </TemplateDestRemoveBtn>
                    )}
                  </TemplateDestRow>
                ))}
                {getTemplateInputConfig(templateModal).multipleAllowed && (
                  <AddItemBtn
                    type="button"
                    onClick={() => setTemplateDestinations([...templateDestinations, ''])}
                    style={{ marginTop: theme.spacing[2] }}
                  >
                    + {t('addAnotherEmail', { defaultValue: 'Adicionar outro email' })}
                  </AddItemBtn>
                )}
              </FormGroup>
              {templateError && (
                <TemplateErrorMsg>{templateError}</TemplateErrorMsg>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={() => setTemplateModal(null)}>
                {t('cancel', { ns: 'common' })}
              </Button>
              <Button
                variant="primary"
                onClick={handleTemplateInstanciate}
                disabled={templateSaving || templateDestinations.every(d => !d.trim())}
              >
                {templateSaving ? '...' : t('addTemplate', { defaultValue: 'Adicionar' })}
              </Button>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* Modal editar allowedDestinations de conector instanciado */}
      {editInstanceModal && (
        <ModalOverlay onClick={() => setEditInstanceModal(null)}>
          <ModalBox maxWidth="480px" onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <BrandIcon icon={editInstanceModal.icon || '🔌'} size={20} style={{ marginRight: 8 }} />
                {editInstanceModal.name || t('editSharedChannel', { defaultValue: 'Editar canal compartilhado' })}
              </ModalTitle>
              <Button variant="ghost" size="sm" onClick={() => setEditInstanceModal(null)}>✕</Button>
            </ModalHeader>
            <ModalBody>
              {editInstanceModal.displayPhone && (
                <TemplateModalMeta>📱 {editInstanceModal.displayPhone}</TemplateModalMeta>
              )}
              {editInstanceModal.displayEmail && (
                <TemplateModalMeta>📧 {editInstanceModal.displayEmail}</TemplateModalMeta>
              )}
              <FormGroup style={{ marginTop: theme.spacing[3] }}>
                <Label style={{ margin: 0 }}>{getTemplateInputConfig(editInstanceModal).label}</Label>
                <FieldHint style={{ marginBottom: theme.spacing[2] }}>
                  {editInstanceModal.type === 'email'
                    ? t('templateDestHintEmail', { defaultValue: 'Informe o(s) endereço(s) de email autorizado(s) para receber mensagens neste canal.' })
                    : editInstanceModal.type === 'telegram'
                      ? t('templateDestHintTelegram', { defaultValue: 'Informe o Chat ID do Telegram autorizado para este canal.' })
                      : t('templateDestHintPhone', { defaultValue: 'Informe o número de telefone autorizado para envio/recebimento neste canal.' })
                  }
                </FieldHint>
                {editInstanceDests.map((dest, idx) => (
                  <TemplateDestRow key={idx}>
                    <Input
                      type={getTemplateInputConfig(editInstanceModal).type}
                      value={dest}
                      onChange={e => {
                        const updated = [...editInstanceDests];
                        updated[idx] = e.target.value;
                        setEditInstanceDests(updated);
                      }}
                      placeholder={getTemplateInputConfig(editInstanceModal).placeholder}
                      autoFocus={idx === 0}
                      style={{
                        flex: 1,
                        borderColor: dest && !validateDestination(dest, editInstanceModal.type)
                          ? theme.colors.danger
                          : undefined,
                      }}
                    />
                    {editInstanceDests.length > 1 && (
                      <TemplateDestRemoveBtn
                        type="button"
                        onClick={() => setEditInstanceDests(editInstanceDests.filter((_, i) => i !== idx))}
                        title={t('remove', { ns: 'common', defaultValue: 'Remover' })}
                      >
                        ✕
                      </TemplateDestRemoveBtn>
                    )}
                  </TemplateDestRow>
                ))}
                {getTemplateInputConfig(editInstanceModal).multipleAllowed && (
                  <AddItemBtn
                    type="button"
                    onClick={() => setEditInstanceDests([...editInstanceDests, ''])}
                    style={{ marginTop: theme.spacing[2] }}
                  >
                    + {t('addAnotherEmail', { defaultValue: 'Adicionar outro email' })}
                  </AddItemBtn>
                )}
              </FormGroup>
              {editInstanceError && (
                <TemplateErrorMsg>{editInstanceError}</TemplateErrorMsg>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={() => setEditInstanceModal(null)}>
                {t('cancel', { ns: 'common' })}
              </Button>
              <Button
                variant="primary"
                onClick={handleEditInstanceSave}
                disabled={editInstanceSaving || editInstanceDests.every(d => !d.trim())}
              >
                {editInstanceSaving ? '...' : t('save', { ns: 'common' })}
              </Button>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* Modal criar credencial */}
      {credModalType && (
        <ModalOverlay onClick={() => setCredModalType(null)}>
          <ModalBox maxWidth="400px" onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{credModalType === 'google_workspace' ? 'Google Workspace' : 'Alexa'} — {t('credentials.newCredential', { defaultValue: 'Nova Credencial' })}</ModalTitle>
              <Button variant="ghost" size="sm" onClick={() => setCredModalType(null)}>✕</Button>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label style={{ margin: 0 }}>{t('credentials.nameLabel', { defaultValue: 'Nome da credencial' })}</Label>
                <Input
                  value={credModalName}
                  onChange={e => setCredModalName(e.target.value)}
                  placeholder={credModalType === 'google_workspace' ? t('credentials.namePlaceholderGoogle', { defaultValue: 'Ex: Conta Google Principal' }) : t('credentials.namePlaceholderAlexa', { defaultValue: 'Ex: Alexa Recepção' })}
                  autoFocus
                />
                <FieldHint>{t('credentials.nameHint', { defaultValue: 'Este nome ajuda a identificar a credencial ao vincular em múltiplos conectores.' })}</FieldHint>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={() => setCredModalType(null)}>{t('cancel', { ns: 'common' })}</Button>
              <Button variant="primary" onClick={createCredential} disabled={credSaving || !credModalName.trim()}>
                {credSaving ? '...' : t('create', { ns: 'common' })}
              </Button>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── Modal credenciais conector ── */}
      {ctModal && (
        <ModalOverlay onClick={() => setCtModal(null)}>
          <ModalBox maxWidth={ctModal.connectorType.schedulingRuleCatalog?.length ? '640px' : '480px'} onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{r(ctModal.connectorType.icon) || '🔌'} {r(ctModal.connectorType.name)}</ModalTitle>
              <Button variant="ghost" size="sm" onClick={() => setCtModal(null)}>✕</Button>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label style={{ margin: 0 }}>{t('instanceName')}</Label>
                <Input value={ctForm.name || ''} onChange={e => setCtForm(f => ({ ...f, name: e.target.value }))} placeholder={r(ctModal.connectorType.name)} />
              </FormGroup>
              
              {/* Renderizar campos de configuração dinamicamente baseado no tipo */}
              {(ctModal.connectorType.configFields || []).map(field => {
                // Campos boolean são renderizados como toggle
                if (field.type === 'boolean') {
                  return (
                    <ToggleRow key={field.name}>
                      <div>
                        <ToggleLabel>{r(field.label) || field.name}</ToggleLabel>
                        <ToggleDesc>{r(field.helpText) || ''}</ToggleDesc>
                      </div>
                      <ToggleSwitch
                        $active={ctForm[field.name] !== false}
                        onClick={() => setCtForm(f => ({ ...f, [field.name]: f[field.name] === false ? true : false }))}
                      >
                        <ToggleThumb $active={ctForm[field.name] !== false} />
                      </ToggleSwitch>
                    </ToggleRow>
                  );
                }

                // Campos list: lista dinâmica de objetos com itemFields
                if (field.type === 'list') {
                  const items = Array.isArray(ctForm[field.name]) ? ctForm[field.name] : [];
                  const addItem = () => {
                    const empty = {};
                    (field.itemFields || []).forEach(f => { empty[f.name] = ''; });
                    setCtForm(prev => ({ ...prev, [field.name]: [...items, empty] }));
                  };
                  const removeItem = (idx) => setCtForm(prev => ({ ...prev, [field.name]: items.filter((_, i) => i !== idx) }));
                  const updateItem = (idx, key, val) => setCtForm(prev => ({
                    ...prev,
                    [field.name]: items.map((item, i) => i === idx ? { ...item, [key]: val } : item)
                  }));
                  return (
                    <FormGroup key={field.name}>
                      <Label style={{ margin: 0 }}>{r(field.label) || field.name}{field.required && ' *'}</Label>
                      {r(field.helpText) && <FieldHint style={{ whiteSpace: 'pre-line', marginBottom: 6 }}>{r(field.helpText)}</FieldHint>}
                      {items.map((item, idx) => (
                        <ListItemRow key={idx}>
                          <ListItemIndex>{idx + 1}</ListItemIndex>
                          <ListItemFields>
                            {(field.itemFields || []).map(f => (
                              <PasswordInput
                                key={f.name}
                                type={f.type === 'password' ? 'password' : 'text'}
                                value={item[f.name] || ''}
                                onChange={e => updateItem(idx, f.name, e.target.value)}
                                placeholder={r(f.placeholder) || r(f.label) || f.name}
                                style={{ marginBottom: 4 }}
                              />
                            ))}
                          </ListItemFields>
                          <ListItemRemove onClick={() => removeItem(idx)} title={t('common:remove')}>×</ListItemRemove>
                        </ListItemRow>
                      ))}
                      <AddItemBtn type="button" onClick={addItem}>
                        {r(field.addLabel) || '+ ' + t('add', { defaultValue: 'Adicionar' })}
                      </AddItemBtn>
                    </FormGroup>
                  );
                }

                // Outros tipos de campo são renderizados como input
                return (
                  <FormGroup key={field.name}>
                    <Label style={{ margin: 0 }}>{r(field.label) || field.name}{field.required && ' *'}</Label>
                    <PasswordInput
                      type={field.type === 'password' ? 'password' : field.type === 'number' ? 'number' : 'text'}
                      value={ctForm[field.name] || ''}
                      onChange={e => setCtForm(f => ({ ...f, [field.name]: e.target.value }))}
                      placeholder={r(field.placeholder) || ''}
                    />
                    {r(field.helpText) && <FieldHint>{r(field.helpText)}</FieldHint>}
                  </FormGroup>
                );
              })}
              {HANDOFF_CHANNEL_TYPES.includes(ctModal.connectorType.type) && (
                <FormGroup>
                  <Label style={{ margin: 0 }}>{t('workingHours', { defaultValue: 'Horário de atendimento humano' })}</Label>
                  <WorkingHoursEditor
                    value={ctForm.workingHours}
                    onChange={(wh) => setCtForm(f => ({ ...f, workingHours: wh }))}
                    t={t}
                    enableLabel={t('workingHoursEnable', { defaultValue: 'Definir horário de atendimento humano neste canal' })}
                    hint={t('workingHoursHint', { defaultValue: 'Usado quando nenhum atendente tem horário próprio. Fora do horário, a IA avisa o cliente e não transfere.' })}
                  />
                  <Label style={{ margin: '12px 0 0' }}>{t('afterHoursMessage', { defaultValue: 'Mensagem fora do horário (transferência)' })}</Label>
                  <Textarea
                    rows={3}
                    value={ctForm.afterHoursHandoffMessage || ''}
                    onChange={e => setCtForm(f => ({ ...f, afterHoursHandoffMessage: e.target.value }))}
                    placeholder={t('afterHoursMessagePlaceholder', { defaultValue: 'Ex: Nosso atendimento humano está fora do horário. A equipe retorna {nextOpenLabel}. Informe isso ao cliente com cordialidade e ofereça ajudar no que for possível agora.' })}
                  />
                  <FieldHint>
                    {t('afterHoursMessageHint', { defaultValue: 'Orientação para a IA quando o cliente pede atendimento humano fora do horário. Use {nextOpenLabel} para inserir o próximo horário de atendimento (ex: "amanhã às 09:00"). Deixe em branco para usar o texto padrão.' })}
                  </FieldHint>
                </FormGroup>
              )}
              {Array.isArray(ctModal.connectorType.schedulingRuleCatalog) && ctModal.connectorType.schedulingRuleCatalog.length > 0 && (
                <SchedulingRulesEditor
                  value={ctForm.schedulingRules || []}
                  onChange={(next) => setCtForm(f => ({ ...f, schedulingRules: next }))}
                  catalog={ctModal.connectorType.schedulingRuleCatalog}
                />
              )}
              {ctModal.connectorType.type === 'google_workspace' && (
                <OAuthInfo>
                  <OAuthInfoIcon>🔐</OAuthInfoIcon>
                  <div>
                    <OAuthInfoTitle>{t('googleOAuthTitle', { defaultValue: 'Autenticação via Google OAuth2' })}</OAuthInfoTitle>
                    <OAuthInfoDesc>
                      {ctModal.existingConnector?.accessToken
                        ? '✅ ' + t('googleOAuthDescAuthorized', { defaultValue: 'Conta Google já autorizada. Salve para atualizar as configurações ou clique em Reutorizar para renovar o acesso.' })
                        : t('googleOAuthDescPending', { defaultValue: 'Após salvar, um botão "Conectar com Google" aparecerá no card do conector. Clique nele para autorizar o acesso à sua conta Google (Calendar, Contacts e Sheets).' })}
                    </OAuthInfoDesc>
                    {ctModal.existingConnector?.accessToken && tenantId && (
                      <OAuthBtn
                        $authorized
                        style={{ marginTop: 8 }}
                        onClick={e => { e.stopPropagation(); window.location.href = getGoogleAuthUrl(); }}
                      >
                        {t('reauthorizeGoogle', { defaultValue: 'Reutorizar Google' })}
                      </OAuthBtn>
                    )}
                  </div>
                </OAuthInfo>
              )}
              {/* WhatsApp Embedded Signup — conexão automatizada via Meta.
                  Renderiza null quando a feature flag/config do Facebook está ausente,
                  mantendo o fluxo manual (campos acima) intacto. */}
              {ctModal.connectorType.type === 'whatsapp' && tenantId && (
                <WhatsAppConnectButton tenantId={tenantId} onConnected={() => { onConnectorsChange?.(); setCtModal(null); }} />
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={() => setCtModal(null)}>{t('cancel', { ns: 'common' })}</Button>
              <Button variant="primary" onClick={handleCtSave} disabled={ctSaving}>{ctSaving ? '...' : t('save', { ns: 'common' })}</Button>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

    </>
  );
};

// ── Layout ────────────────────────────────────────────────────────────────────
const AgentTabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid ${theme.colors.border};
  margin-bottom: ${theme.spacing[5]};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  flex-shrink: 0;
  min-height: 36px;
  &::-webkit-scrollbar { display: none; }
`;
const AgentTab = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: none;
  border: none;
  border-bottom: 3px solid ${p => p.$active ? theme.colors.primary : 'transparent'};
  color: ${p => p.$active ? theme.colors.primary : theme.colors.textSecondary};
  font-size: ${theme.typography.sm};
  font-weight: ${p => p.$active ? theme.typography.semibold : theme.typography.normal};
  cursor: pointer;
  margin-bottom: -2px;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  white-space: nowrap;
  flex-shrink: 0;
  transition: ${theme.transitions.fast};
  &:hover { color: ${theme.colors.primary}; }
`;
const AgentTabBadge = styled.span`
  background: ${theme.colors.primary};
  color: white;
  font-size: 10px;
  font-weight: 600;
  border-radius: ${theme.radii.full};
  padding: 1px 6px;
  line-height: 1.4;
`;
const AgentTabWarn = styled.span`
  font-size: 12px;
  line-height: 1;
  cursor: help;
`;
const AgentTabCheck = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${theme.colors.success};
  line-height: 1;
`;
const IdentityCol = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[3]};`;
const ConnectorsTab = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[3]};`;
const ConnectorGrid2Col = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(280px, 100%), 1fr));
  gap: ${theme.spacing[3]};
`;

// Lista compacta de conectores (substitui ConnectorGrid2Col na aba Conectores)
const ConnectorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

// Section label for separating installed vs available connectors
const ConnSectionLabel = styled.div`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: ${theme.spacing[2]} 0 ${theme.spacing[1]} 0;
  margin-top: ${theme.spacing[2]};
  border-top: 1px solid ${theme.colors.border};
`;

// ── Tipografia ────────────────────────────────────────────────────────────────
// SectionLabel, FieldHint, ToggleRow/Label/Desc/Switch/Thumb — importados de ui.js
const LabelWithHelp = styled.div`display: flex; align-items: center; gap: ${theme.spacing[1]};`;
const ReadOnly = styled.div`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textMuted};
  font-family: monospace;
`;
const Textarea = styled.textarea`
  width: 100%; padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm}; font-family: inherit; resize: vertical;
  box-sizing: border-box; outline: none;
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;
const CharCounter = styled.div`
  font-size: ${theme.typography.xs};
  color: ${p => p.$warn ? theme.colors.warning : theme.colors.textMuted};
  text-align: right;
  margin-top: 2px;
`;
const RequiredMark = styled.span`color: ${theme.colors.danger};`;
const BasePromptCollapsible = styled.div`
  border: 1px solid ${theme.colors.warningBorder};
  border-radius: ${theme.radii.md};
  background: ${theme.colors.warningLight};
  overflow: hidden;
  margin-bottom: ${theme.spacing[2]};
`;
const BasePromptToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: #fef3c7;
  border: none;
  cursor: pointer;
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  color: #92400e;
  text-align: left;
  transition: ${theme.transitions.fast};
  &:hover { background: #fde68a; }
`;
const BasePromptText = styled.pre`
  font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  margin: 0; white-space: pre-wrap; font-family: inherit; line-height: 1.5;
  max-height: 200px; overflow-y: auto;
  border-top: 1px solid ${theme.colors.warningBorder};
`;

// ── Connector cards ───────────────────────────────────────────────────────────
const ConnectorGrid = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[2]};`;

const ConnCard = styled.div`
  border: 1.5px solid ${p => p.$linked ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.radii.lg};
  background: ${p => p.$linked ? theme.colors.primaryLight : theme.colors.white};
  overflow: hidden;
  transition: ${theme.transitions.base};
  &:hover { border-color: ${theme.colors.primary}; box-shadow: ${theme.shadows.sm}; }
`;
const ConnCardMain = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  cursor: pointer;
  min-width: 0;
`;
const ConnIcon = styled.div`font-size: 20px; line-height: 1; flex-shrink: 0;`;
const ConnInfo = styled.div`flex: 1; min-width: 0;`;
const ConnName = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const ConnType = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const ConnSubtitle = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const ConnRight = styled.div`display: flex; align-items: center; gap: ${theme.spacing[1]}; flex-shrink: 0; flex-wrap: wrap; justify-content: flex-end;`;
const ConnDetail = styled.div`
  border-top: 1px solid ${theme.colors.border};
  background: ${theme.colors.gray50};
  padding: ${theme.spacing[3]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;
const ExpandBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 22px; height: 22px;
  background: none; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  color: ${theme.colors.textMuted}; cursor: pointer;
  flex-shrink: 0;
  transition: ${theme.transitions.fast};
  &:hover { border-color: ${theme.colors.primary}; color: ${theme.colors.primary}; }
`;
const ConnDesc = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary}; padding: 0 ${theme.spacing[3]} ${theme.spacing[2]};`;
const WebhookRow = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.gray50};
  border-top: 1px solid ${theme.colors.border};
`;
const WebhookLabel = styled.div`font-size: 10px; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textMuted}; white-space: nowrap; text-transform: uppercase; letter-spacing: 0.04em;`;
const WebhookUrl = styled.div`font-size: 10px; color: ${theme.colors.textSecondary}; font-family: monospace; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0;`;
const WebhookCopy = styled.button`background: none; border: none; cursor: pointer; padding: 2px; color: ${theme.colors.textMuted}; display: flex; align-items: center; flex-shrink: 0; &:hover { color: ${theme.colors.primary}; }`;
const OAuthBtn = styled.button`padding: 3px ${theme.spacing[2]}; border-radius: ${theme.radii.md}; border: 1px solid ${p => p.$authorized ? theme.colors.success : theme.colors.warning}; background: ${p => p.$authorized ? '#f0fdf4' : '#fffbeb'}; color: ${p => p.$authorized ? theme.colors.success : '#92400e'}; font-size: 10px; font-weight: 600; cursor: pointer; white-space: nowrap; flex-shrink: 0; &:hover { opacity: 0.85; }`;
const OAuthInfo = styled.div`display: flex; gap: ${theme.spacing[3]}; padding: ${theme.spacing[3]}; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: ${theme.radii.lg}; margin-top: ${theme.spacing[2]};`;
const OAuthInfoIcon = styled.div`font-size: 20px; flex-shrink: 0;`;
const OAuthInfoTitle = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: #1e40af; margin-bottom: 4px;`;
const OAuthInfoDesc = styled.div`font-size: ${theme.typography.xs}; color: #1e3a8a; line-height: 1.4;`;
const RemoveBtn = styled.div`
  width: 18px; height: 18px; border-radius: 50%;
  background: ${theme.colors.danger}; color: white;
  font-size: 9px; display: flex; align-items: center; justify-content: center;
  cursor: pointer; opacity: 0.75; flex-shrink: 0;
  &:hover { opacity: 1; }
`;

// ── Tools ─────────────────────────────────────────────────────────────────────
const ToolsToggle = styled.div`
  display: flex; align-items: center; gap: 4px;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};
  border-top: 1px solid ${theme.colors.border};
  cursor: pointer; user-select: none;
  &:hover { color: ${theme.colors.primary}; background: ${theme.colors.gray50}; }
`;
const ToolsPanel = styled.div`
  border-top: 1px solid ${theme.colors.border};
  padding: ${theme.spacing[2]};
  display: flex; flex-direction: column; gap: ${theme.spacing[1]};
  background: ${theme.colors.gray50};
`;
const ToolRow = styled.div`
  display: flex; align-items: flex-start; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]};
  border-radius: ${theme.radii.md};
  background: ${p => p.$active ? theme.colors.primaryLight : 'transparent'};
  border: 1px solid ${p => p.$active ? theme.colors.primary : 'transparent'};
  transition: ${theme.transitions.fast};
`;
const ToolCheck = styled.div`
  width: 16px; height: 16px; border-radius: ${theme.radii.sm}; flex-shrink: 0; margin-top: 1px;
  border: 2px solid ${p => p.$active ? theme.colors.primary : theme.colors.border};
  background: ${p => p.$active ? theme.colors.primary : 'white'};
  color: white; display: flex; align-items: center; justify-content: center; font-size: 10px;
`;
const ToolInfo = styled.div`flex: 1; min-width: 0;`;
const ToolName = styled.div`font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const ToolDesc = styled.div`font-size: 11px; color: ${theme.colors.textMuted}; line-height: 1.3;`;
const ToolsHint = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; font-style: italic; padding: ${theme.spacing[1]};`;

// ── Tools tab ─────────────────────────────────────────────────────────────────
const ToolsEmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: ${theme.spacing[3]}; padding: ${theme.spacing[8]} ${theme.spacing[4]};
  text-align: center;
`;
const ToolsEmptyText = styled.div`
  font-size: ${theme.typography.sm}; color: ${theme.colors.textMuted}; max-width: 300px; line-height: 1.5;
`;
const ToolGroupCard = styled.div`
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
  overflow: hidden;
  background: ${theme.colors.white};
`;
const ToolGroupHeader = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.gray50};
  border-bottom: 1px solid ${theme.colors.border};
`;
const ToolGroupName = styled.div`
  flex: 1; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;
const ToolGroupCount = styled.div`
  font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};
  background: ${theme.colors.white}; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.full}; padding: 1px ${theme.spacing[2]};
`;
const ToolGroupList = styled.div`
  display: flex; flex-direction: column;
`;
const ToolToggleRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: ${theme.spacing[3]}; padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border};
  &:last-child { border-bottom: none; }
  &:hover { background: ${theme.colors.gray50}; }
  transition: background 0.15s;
`;
const ToolToggleInfo = styled.div`flex: 1; min-width: 0;`;
const ToolToggleName = styled.div`
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;
const ToolToggleDesc = styled.div`
  font-size: 11px; color: ${theme.colors.textMuted}; line-height: 1.3; margin-top: 1px;
`;
const ToolOutputFields = styled.div`
  margin-top: 4px; padding: 4px 8px; background: ${theme.colors.gray50}; border-radius: ${theme.radii.sm}; border: 1px solid ${theme.colors.border};
`;
const ToolOutputTitle = styled.div`font-size: 10px; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textSecondary}; margin-bottom: 2px;`;
const ToolOutputField = styled.div`font-size: 10px; color: ${theme.colors.textMuted}; line-height: 1.5; code { background: ${theme.colors.primaryLight}; color: ${theme.colors.primary}; padding: 0 3px; border-radius: 2px; font-size: 9px; }`;

// ── Auth-required tools ───────────────────────────────────────────────────────
const ToolToggleActions = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]};`;
const AuthLockBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: ${theme.radii.sm};
  border: 1px solid ${({ $active }) => $active ? theme.colors.warning : theme.colors.border};
  background: ${({ $active }) => $active ? theme.colors.warningLight || '#fef3c7' : 'transparent'};
  color: ${({ $active }) => $active ? theme.colors.warning || '#d97706' : theme.colors.textMuted};
  cursor: pointer; transition: all 0.15s;
  &:hover { border-color: ${theme.colors.warning || '#d97706'}; color: ${theme.colors.warning || '#d97706'}; }
`;
const AuthBadge = styled.span`
  margin-left: 6px; font-size: 10px; vertical-align: middle;
`;

// ── List field ────────────────────────────────────────────────────────────────
const ListItemRow = styled.div`
  display: flex; align-items: flex-start; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]};
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  margin-bottom: ${theme.spacing[2]};
`;
const ListItemIndex = styled.div`
  width: 20px; height: 20px; border-radius: 50%; flex-shrink: 0; margin-top: 6px;
  background: ${theme.colors.primary}; color: white;
  font-size: 10px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
`;
const ListItemFields = styled.div`flex: 1; display: flex; flex-direction: column; gap: 4px;`;
const ListItemRemove = styled.button`
  background: none; border: none; cursor: pointer; flex-shrink: 0; margin-top: 4px;
  color: ${theme.colors.danger}; font-size: 16px; line-height: 1; padding: 2px 4px;
  &:hover { opacity: 0.7; }
`;
const AddItemBtn = styled.button`
  display: inline-flex; align-items: center; gap: 4px;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: none; border: 1.5px dashed ${theme.colors.primary};
  border-radius: ${theme.radii.md}; color: ${theme.colors.primary};
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};
  cursor: pointer; width: 100%;
  justify-content: center;
  &:hover { background: ${theme.colors.primaryLight}; }
`;

// ── Template cards (available for instanciation) ──────────────────────────────
const TemplateGroup = styled.div`
  display: flex; flex-direction: column; gap: ${theme.spacing[2]};
`;
const TemplateGroupLabel = styled.div`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textSecondary};
  padding: ${theme.spacing[1]} 0;
`;
const TemplateCardList = styled.div`
  display: flex; flex-direction: column; gap: ${theme.spacing[2]};
`;
const TemplateCard = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  border: 1.5px dashed ${theme.colors.border};
  border-radius: ${theme.radii.lg};
  background: ${theme.colors.white};
  transition: ${theme.transitions.base};
  &:hover { border-color: ${theme.colors.primary}; background: ${theme.colors.primaryLight}; }
`;
const TemplateCardIcon = styled.div`
  font-size: 22px; line-height: 1; flex-shrink: 0;
`;
const TemplateCardInfo = styled.div`
  flex: 1; min-width: 0;
`;
const TemplateCardName = styled.div`
  font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const TemplateCardDesc = styled.div`
  font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};
  line-height: 1.3; margin-top: 2px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const TemplateCardMeta = styled.div`
  font-size: 10px; color: ${theme.colors.textSecondary};
  font-family: monospace; margin-top: 2px;
`;
const TemplateAddBtn = styled.button`
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${theme.colors.primary}; color: white;
  border: none; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};
  cursor: pointer; white-space: nowrap; flex-shrink: 0;
  transition: ${theme.transitions.fast};
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ── Template instanciation modal ──────────────────────────────────────────────
const TemplateModalDesc = styled.div`
  font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};
  line-height: 1.4; margin-bottom: ${theme.spacing[2]};
`;
const TemplateModalMeta = styled.div`
  font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary};
  font-family: monospace; margin-bottom: ${theme.spacing[1]};
`;
const TemplateDestRow = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[2]};
`;
const TemplateDestRemoveBtn = styled.button`
  background: none; border: 1px solid ${theme.colors.danger};
  color: ${theme.colors.danger}; border-radius: ${theme.radii.md};
  width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;
  font-size: 11px; cursor: pointer; flex-shrink: 0;
  &:hover { background: ${theme.colors.dangerLight}; }
`;
const TemplateErrorMsg = styled.div`
  font-size: ${theme.typography.xs}; color: ${theme.colors.danger};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.dangerLight};
  border: 1px solid ${theme.colors.dangerBorder};
  border-radius: ${theme.radii.md};
  margin-top: ${theme.spacing[2]};
`;

export default AgentForm;

// ── Alexa Activation ─────────────────────────────────────────────────────────

function AlexaActivation({ tenantId, connector }) {
  const { t } = useTranslation(['agents', 'common']);
  const [code, setCode]           = React.useState(null);
  const [secondsLeft, setSeconds] = React.useState(0);
  const [loading, setLoading]     = React.useState(false);
  const timerRef                  = React.useRef(null);

  React.useEffect(() => () => clearInterval(timerRef.current), []);

  const generate = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const res = await api.post(`/connectors/${tenantId}/alexa/activation-code`);
      const { code: c, expiresInSeconds } = res.data;
      setCode(c);
      setSeconds(expiresInSeconds);
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setSeconds(s => {
          if (s <= 1) { clearInterval(timerRef.current); setCode(null); return 0; }
          return s - 1;
        });
      }, 1000);
    } catch (err) {
      console.error('alexa activation error', err);
    } finally { setLoading(false); }
  };

  const isLinked = !!connector.alexaUserId;
  const mins = Math.floor(secondsLeft / 60);
  const secs = String(secondsLeft % 60).padStart(2, '0');

  return (
    <AlexaRow onClick={e => e.stopPropagation()}>
      {code ? (
        <>
          <AlexaCodeBox>
            <AlexaCodeLabel>{t('alexa.activationCode', { defaultValue: 'Código de ativação' })}</AlexaCodeLabel>
            <AlexaCode>{code.replace(/(.{3})/g, '$1 ').trim()}</AlexaCode>
            <AlexaExpiry $urgent={secondsLeft < 60}>
              {t('alexa.expiresIn', { defaultValue: 'Expira em' })} {mins}:{secs}
            </AlexaExpiry>
          </AlexaCodeBox>
          <OAuthBtn onClick={generate} disabled={loading}>
            {t('alexa.newCode', { defaultValue: 'Novo código' })}
          </OAuthBtn>
        </>
      ) : (
        <>
          <WebhookLabel>{isLinked ? '✅ ' + t('alexa.linked', { defaultValue: 'Alexa vinculada' }) : '⚠️ ' + t('alexa.notLinked', { defaultValue: 'Alexa não vinculada' })}</WebhookLabel>
          <OAuthBtn $authorized={isLinked} onClick={generate} disabled={loading}>
            {loading ? '...' : isLinked ? t('alexa.generateNewCode', { defaultValue: 'Gerar novo código' }) : t('alexa.linkAlexa', { defaultValue: 'Vincular Alexa' })}
          </OAuthBtn>
        </>
      )}
    </AlexaRow>
  );
}

const AlexaRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: #f5f3ff; border-top: 1px solid ${theme.colors.purpleLight};
`;
const AlexaCodeBox = styled.div`display: flex; align-items: center; gap: ${theme.spacing[3]}; flex: 1;`;
const AlexaCodeLabel = styled.div`font-size: 10px; font-weight: 600; color: ${theme.colors.purple700}; text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap;`;
const AlexaCode = styled.div`font-family: monospace; font-size: ${theme.typography.base}; font-weight: 700; color: ${theme.colors.purple900}; letter-spacing: 4px;`;
const AlexaExpiry = styled.div`font-size: 10px; color: ${p => p.$urgent ? theme.colors.danger : theme.colors.textMuted}; white-space: nowrap;`;

// ── Link público compacto (novo) ─────────────────────────────────────────────────────────────────────────────────────
const PublicLinkCompact = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]};
  background: #f5f3ff;
  border: 1px solid ${theme.colors.purpleBorder};
  border-radius: ${theme.radii.lg};
`;
const PublicLinkCompactRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
  @media (max-width: 480px) {
    & > span { font-size: 10px; max-width: 100%; overflow: hidden; text-overflow: ellipsis; }
  }
`;
const PublicLinkCompactHint = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.purple700};
  line-height: 1.4;
`;
const PublicLinkAdvancedToggle = styled.button`
  background: none; border: none; padding: 0;
  font-size: ${theme.typography.xs};
  color: ${theme.colors.purple600}; cursor: pointer; font-weight: ${theme.typography.semibold};
  &:hover { text-decoration: underline; }
`;
const PublicLinkAdvanced = styled.div`
  display: flex; flex-direction: column; gap: ${theme.spacing[3]};
  padding-top: ${theme.spacing[2]};
  border-top: 1px solid ${theme.colors.purpleBorder};
`;
const PublicLinkCopyBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; flex-shrink: 0;
  background: white; border: 1px solid ${theme.colors.purpleBorder};
  border-radius: ${theme.radii.md}; color: ${theme.colors.purple700}; cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.purpleLight}; }
`;

const PublicLinkBox = styled.div`
  display: flex; flex-direction: column; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: linear-gradient(135deg, #f0f0ff 0%, #faf5ff 100%);
  border: 1px solid ${theme.colors.purpleBorder};
  border-radius: ${theme.radii.xl};
`;
const PublicLinkHeader = styled.div`display: flex; flex-direction: column; gap: 4px;`;
const PublicLinkTitle  = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.purple900};`;
const PublicLinkHint   = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.purple700}; line-height: 1.4;`;
const PublicLinkInputRow = styled.div`display: flex; align-items: stretch;`;
const PublicLinkPrefix = styled.div`
  display: flex; align-items: center;
  padding: 0 ${theme.spacing[2]};
  background: ${theme.colors.gray100}; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md} 0 0 ${theme.radii.md};
  font-size: 11px; color: ${theme.colors.textMuted}; white-space: nowrap;
`;
const PublicLinkPreview = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: white; border: 1px solid ${theme.colors.purpleBorder}; border-radius: ${theme.radii.md};
`;
const CopySlugBtn = styled.button`
  padding: 4px ${theme.spacing[3]};
  background: #6366f1; color: white; border: none;
  border-radius: ${theme.radii.md}; font-size: 11px; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  &:hover { background: #4f46e5; }
`;

const RemoveSlugBtn = styled.button`
  padding: 2px ${theme.spacing[2]};
  background: none; color: ${theme.colors.danger};
  border: 1px solid ${theme.colors.dangerBorder};
  border-radius: ${theme.radii.md}; font-size: 11px;
  cursor: pointer; white-space: nowrap;
  &:hover { background: ${theme.colors.dangerLight}; }
`;
